import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import i18n from "./i18n/i18n.js";
import { IRRIG_CROPS } from "./IrrigationCalc";
import { tCrop } from "./cropNames.js";
import { printFieldReport } from "./printReport.js";

const API = window.location.hostname === "localhost"
  ? "http://localhost:8000"
  : "";

// ── Helpers ───────────────────────────────────────────────────────────────────
function getToken()       { return localStorage.getItem("of_admin_token"); }
function setToken(t)      { localStorage.setItem("of_admin_token", t); }
function clearToken()     { localStorage.removeItem("of_admin_token"); }

// ── Shared styles ─────────────────────────────────────────────────────────────
const S = {
  page:    { minHeight: "100vh", background: "#f5f9f3", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "system-ui, sans-serif" },
  card:    { background: "#fff", borderRadius: 14, padding: "36px 40px", boxShadow: "0 4px 24px rgba(0,0,0,0.08)", width: 380, maxWidth: "92vw" },
  logo:    { fontSize: 22, fontWeight: 700, color: "#1a2e1a", marginBottom: 4 },
  sub:     { fontSize: 13, color: "#7a9a6a", marginBottom: 28 },
  label:   { fontSize: 12, fontWeight: 600, color: "#4a6a3a", marginBottom: 6, display: "block" },
  input:   { width: "100%", padding: "10px 12px", fontSize: 14, border: "1px solid #c8d8c0", borderRadius: 8, outline: "none", boxSizing: "border-box", fontFamily: "inherit" },
  btn:     { width: "100%", padding: "11px", fontSize: 14, fontWeight: 600, background: "#2d5a1b", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", marginTop: 16 },
  btnGhost:{ padding: "8px 16px", fontSize: 12, fontWeight: 600, background: "transparent", color: "#5a7a4a", border: "1px solid #c8d8c0", borderRadius: 6, cursor: "pointer" },
  err:     { background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", borderRadius: 7, padding: "10px 12px", fontSize: 13, marginTop: 12 },
  ok:      { background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#15803d", borderRadius: 7, padding: "10px 12px", fontSize: 13, marginTop: 12 },
  divider: { height: 1, background: "#e8f0e0", margin: "20px 0" },
};

// ── Skeleton loader primitives ────────────────────────────────────────────────
const skeletonKeyframes = `
@keyframes _sk_shimmer {
  0%   { background-position: -400px 0; }
  100% { background-position:  400px 0; }
}`;
if (typeof document !== "undefined" && !document.getElementById("_sk_style")) {
  const s = document.createElement("style");
  s.id = "_sk_style";
  s.textContent = skeletonKeyframes;
  document.head.appendChild(s);
}

function Sk({ w = "100%", h = 14, r = 6, mb = 0 }) {
  return (
    <div style={{
      width: w, height: h, borderRadius: r, marginBottom: mb,
      background: "linear-gradient(90deg,#e8ede4 25%,#f2f6ee 50%,#e8ede4 75%)",
      backgroundSize: "800px 100%",
      animation: "_sk_shimmer 1.4s ease-in-out infinite",
      flexShrink: 0,
    }} />
  );
}

// Pre-built skeleton shapes for common UI patterns
function SkRow({ cols = 4, gap = 12, mb = 10 }) {
  const widths = ["40%", "25%", "15%", "12%", "8%"];
  return (
    <div style={{ display: "flex", gap, alignItems: "center", marginBottom: mb }}>
      {Array.from({ length: cols }, (_, i) => (
        <Sk key={i} w={widths[i] || "20%"} h={13} r={4} />
      ))}
    </div>
  );
}
function SkCard({ lines = 3, mb = 12 }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #e8f0e0", borderRadius: 10, padding: "14px 16px", marginBottom: mb }}>
      <Sk w="55%" h={14} r={5} mb={10} />
      {Array.from({ length: lines - 1 }, (_, i) => (
        <Sk key={i} w={i % 2 === 0 ? "85%" : "65%"} h={11} r={4} mb={7} />
      ))}
    </div>
  );
}
function SkTable({ rows = 4, cols = 4 }) {
  return (
    <div>
      <div style={{ display: "flex", gap: 12, marginBottom: 10, padding: "6px 10px", background: "#f5f9f3", borderRadius: 6 }}>
        {Array.from({ length: cols }, (_, i) => <Sk key={i} w={`${100 / cols}%`} h={11} r={4} />)}
      </div>
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} style={{ display: "flex", gap: 12, marginBottom: 8, padding: "4px 10px" }}>
          {Array.from({ length: cols }, (_, c) => <Sk key={c} w={`${100 / cols}%`} h={12} r={4} />)}
        </div>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SIGN-IN PAGE  (email + password primary; magic link as fallback)
// ══════════════════════════════════════════════════════════════════════════════
function SignInPage({ onAuth, onBack }) {
  const [email,      setEmail]      = useState("");
  const [password,   setPassword]   = useState("");
  const [mode,       setMode]       = useState("login");   // "login" | "magic" | "request_sent" | "magic_sent"
  const [error,      setError]      = useState(null);
  const [loading,    setLoading]    = useState(false);

  async function handleLogin(e) {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setLoading(true); setError(null);
    try {
      const res = await axios.post(`${API}/auth/login`, { email: email.trim().toLowerCase(), password });
      setToken(res.data.jwt);
      // Fetch full user profile (including orgs + is_org_admin) before entering dashboard
      const me = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${res.data.jwt}` } });
      onAuth(me.data);
    } catch (err) {
      const detail = err.response?.data?.detail || err.response?.data?.error || "";
      if (detail === "no_password") {
        // Authorized but no password set — go to forgot password flow
        setMode("forgot");
      } else {
        setError("Incorrect email or password.");
      }
    }
    setLoading(false);
  }

  const [orgId,   setOrgId]   = useState(0);
  const [orgName, setOrgName] = useState("");

  // Resolve invite token from URL — auto-switch to magic link mode
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const inv = p.get("invite");
    if (inv) {
      axios.get(`${API}/auth/org-by-invite/${inv}`)
        .then(r => { setOrgId(r.data.id); setOrgName(r.data.name); setMode("magic"); })
        .catch(() => setMode("bad_invite"));
    }
  }, []);

  async function sendMagicLink() {
    const emailAddr = email.trim().toLowerCase();
    if (!emailAddr) { setError("Enter your email address."); return; }
    setLoading(true); setError(null);
    try {
      const res = await axios.post(`${API}/auth/request-access`, { email: emailAddr, org_id: orgId });
      if (res.data.status === "magic_link_sent") setMode("magic_sent");
      else if (res.data.status === "request_sent") setMode("request_sent");
      else setError(res.data.error || "Unexpected response");
    } catch (err) {
      setError("Something went wrong. Try again.");
    }
    setLoading(false);
  }

  if (mode === "bad_invite") return (
    <div style={S.page}><div style={S.card}>
      <div style={{ fontSize:40, textAlign:"center", marginBottom:8 }}>🔗</div>
      <div style={S.logo}>OpenFarm Analytics</div>
      <div style={{ ...S.err, textAlign:"center", marginTop:0 }}>
        This invite link is invalid or has expired.
      </div>
      <div style={{ fontSize:12, color:"#6b7280", textAlign:"center", marginTop:8, lineHeight:1.6 }}>
        Ask your organization admin to share a fresh invite link, or sign in if you already have an account.
      </div>
      <div style={S.divider} />
      <button onClick={() => { setMode("login"); window.history.replaceState({}, "", window.location.pathname); }} style={S.btn}>
        Go to sign in
      </button>
      <button onClick={onBack} style={{ ...S.btnGhost, marginTop:8 }}>← Back to app</button>
    </div></div>
  );

  if (mode === "magic_sent") return (
    <div style={S.page}><div style={S.card}>
      <div style={S.logo}>OpenFarm Analytics</div>
      <div style={S.ok}>✓ Check your inbox at <strong>{email}</strong> and click the link to sign in.</div>
      <div style={S.divider} />
      <button onClick={onBack} style={S.btnGhost}>← Back to app</button>
    </div></div>
  );

  if (mode === "request_sent") return (
    <div style={S.page}><div style={S.card}>
      <div style={S.logo}>OpenFarm Analytics</div>
      <div style={S.ok}>✓ Access requested. You'll receive an email once your admin approves you.</div>
      <div style={S.divider} />
      <button onClick={onBack} style={S.btnGhost}>← Back to app</button>
    </div></div>
  );

  // "forgot" mode — user has an account but no password (or forgot it)
  // We verify their email exists before sending a magic link
  if (mode === "forgot") return (
    <div style={S.page}><div style={S.card}>
      <div style={S.logo}>OpenFarm Analytics</div>
      <div style={S.sub}>Enter your email to receive a sign-in link</div>
      <label style={S.label}>Email address</label>
      <input type="email" value={email} onChange={e => setEmail(e.target.value)}
        placeholder="you@example.com" style={S.input} autoFocus />
      {error && <div style={S.err}>{error}</div>}
      <button onClick={sendMagicLink} disabled={loading}
        style={S.btn}>{loading ? "Sending…" : "Send magic link"}</button>
      <div style={S.divider} />
      <button onClick={() => { setMode("login"); setError(null); }} style={S.btnGhost}>← Back to sign in</button>
    </div></div>
  );

  // "magic" mode — new user coming from an invite link
  if (mode === "magic") return (
    <div style={S.page}><div style={S.card}>
      <div style={S.logo}>OpenFarm Analytics</div>
      {orgName
        ? <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 7, padding: "8px 12px", fontSize: 13, marginBottom: 20, color: "#15803d" }}>
            Joining: <strong>{orgName}</strong>
          </div>
        : <div style={S.err}>You need an invite link to request access. Contact your organization admin.</div>
      }
      {(orgName || orgId > 0) && <>
        <label style={S.label}>Email address</label>
        <input type="email" value={email} onChange={e => setEmail(e.target.value)}
          placeholder="you@example.com" style={S.input} autoFocus />
        {error && <div style={S.err}>{error}</div>}
        <button onClick={sendMagicLink} disabled={loading}
          style={S.btn}>{loading ? "Sending…" : "Request access"}</button>
      </>}
      <div style={S.divider} />
      <button onClick={() => { setMode("login"); setError(null); }} style={S.btnGhost}>← Back to sign in</button>
    </div></div>
  );

  // Default: email + password login
  return (
    <div style={S.page}>
      <div style={S.card}>
        <div style={S.logo}>OpenFarm Analytics</div>
        <div style={S.sub}>Sign in to your dashboard</div>
        <form onSubmit={handleLogin}>
          <label style={S.label} htmlFor="email">Email address</label>
          <input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)}
            placeholder="you@example.com" style={S.input} autoFocus />
          <label style={{ ...S.label, marginTop: 14 }} htmlFor="pw">Password</label>
          <input id="pw" type="password" value={password} onChange={e => setPassword(e.target.value)}
            placeholder="••••••••" style={S.input} />
          {error && <div style={S.err}>{error}</div>}
          <button type="submit" style={S.btn} disabled={loading}>
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
        <div style={{ textAlign: "center", marginTop: 14 }}>
          <button onClick={() => { setMode("forgot"); setError(null); }}
            style={{ background: "none", border: "none", color: "#5a8a3a", fontSize: 12, cursor: "pointer", textDecoration: "underline" }}>
            Forgot password?
          </button>
        </div>
        <div style={S.divider} />
        <button onClick={onBack} style={S.btnGhost}>← Back to app</button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SET PASSWORD  (shown after first magic link sign-in)
// ══════════════════════════════════════════════════════════════════════════════
function SetPasswordPage({ user, jwt, onDone, orgName }) {
  const [pw,      setPw]      = useState("");
  const [confirm, setConfirm] = useState("");
  const [error,   setError]   = useState(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (pw !== confirm) { setError("Passwords don't match."); return; }
    if (pw.length < 8)  { setError("Password must be at least 8 characters."); return; }
    setLoading(true); setError(null);
    try {
      await axios.post(`${API}/auth/set-password`, { password: pw },
        { headers: { Authorization: `Bearer ${jwt}` } });
      onDone();
    } catch (err) {
      setError(err.response?.data?.error || "Something went wrong.");
    }
    setLoading(false);
  }

  return (
    <div style={S.page}>
      <div style={S.card}>
        <div style={S.logo}>{orgName ? `Welcome to ${orgName}` : "Welcome to OpenFarm"}</div>
        <div style={S.sub}>Set a password for <strong>{user.email}</strong> so you can sign in quickly next time.</div>
        <form onSubmit={handleSubmit}>
          <label style={S.label}>New password</label>
          <input type="password" value={pw} onChange={e => setPw(e.target.value)}
            placeholder="At least 8 characters" style={S.input} autoFocus />
          <label style={{ ...S.label, marginTop: 14 }}>Confirm password</label>
          <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)}
            placeholder="Repeat password" style={S.input} />
          {error && <div style={S.err}>{error}</div>}
          <button type="submit" style={S.btn} disabled={loading}>
            {loading ? "Saving…" : "Set password & continue"}
          </button>
        </form>
        <div style={{ textAlign: "center", marginTop: 12 }}>
          <button onClick={onDone}
            style={{ background: "none", border: "none", color: "#999", fontSize: 12, cursor: "pointer" }}>
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAGIC LINK HANDLER (called on page load if ?token= in URL)
// ══════════════════════════════════════════════════════════════════════════════
function MagicLinkHandler({ token, onAuth, onBack }) {
  const [status,   setStatus]   = useState("verifying");
  const [userData, setUserData] = useState(null);
  const [jwt,      setJwt]      = useState(null);
  const [orgName,  setOrgName]  = useState("");

  useEffect(() => {
    axios.post(`${API}/auth/verify-token`, { token })
      .then(res => {
        if (res.data.jwt) {
          setToken(res.data.jwt);
          setJwt(res.data.jwt);
          setUserData(res.data.user);
          setOrgName(res.data.org_name || "");
          setStatus("set_password");
        } else {
          setStatus("invalid");
        }
      })
      .catch(() => setStatus("invalid"));
  }, [token]);

  if (status === "set_password" && userData) return (
    <SetPasswordPage user={userData} jwt={jwt} orgName={orgName} onDone={() => onAuth(userData)} />
  );

  if (status === "verifying") return (
    <div style={S.page}>
      <div style={S.card}>
        <div style={S.logo}>OpenFarm Analytics</div>
        <div style={S.sub}>Verifying your link…</div>
      </div>
    </div>
  );

  return (
    <div style={S.page}>
      <div style={S.card}>
        <div style={{ fontSize:40, textAlign:"center", marginBottom:8 }}>⏱️</div>
        <div style={S.logo}>OpenFarm Analytics</div>
        <div style={{ ...S.err, textAlign:"center", marginTop:0 }}>
          This sign-in link is invalid or has expired.
        </div>
        <div style={{ fontSize:12, color:"#6b7280", textAlign:"center", marginTop:8, lineHeight:1.6 }}>
          Magic links expire after 24 hours and can only be used once. Request a new one below.
        </div>
        <div style={S.divider} />
        <button onClick={() => { window.history.replaceState({}, "", window.location.pathname); onBack(); }}
          style={S.btn}>
          Request a new link
        </button>
        <button onClick={onBack} style={{ ...S.btnGhost, marginTop:8 }}>← Back to app</button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// DASHBOARD SHELL (placeholder — will be filled in next)
// ══════════════════════════════════════════════════════════════════════════════
const OPENFARM_SLUG = "openfarm";

function DashboardShell({ user, onSignOut, onDisease }) {
  const { t } = useTranslation();
  const [tab,         setTab]         = useState("advanced");
  const [activeOrg,   setActiveOrg]   = useState(user.orgs?.length > 0 ? user.orgs[0] : null);
  const [lang,        setLang]        = useState(i18n.language || "en");
  const [printResult, setPrintResult] = useState(null);
  const [menuOpen,    setMenuOpen]    = useState(false);
  const [moreOpen,    setMoreOpen]    = useState(false);
  const isMobile = typeof window !== "undefined" && window.innerWidth < 700;

  // No orgs and not global admin — show a holding page
  if (!user.admin && (!user.orgs || user.orgs.length === 0)) return (
    <div style={{ minHeight: "100vh", background: "#f0f6ea", fontFamily: "system-ui, sans-serif", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#fff", borderRadius: 14, padding: "36px 40px", boxShadow: "0 4px 24px rgba(0,0,0,0.08)", maxWidth: 400, textAlign: "center" }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: "#1a2e1a", marginBottom: 8 }}>You're in!</div>
        <div style={{ fontSize: 13, color: "#7a9a6a", marginBottom: 24, lineHeight: 1.6 }}>
          Your account is active but you haven't been added to an organization yet. Contact your admin to be assigned to one.
        </div>
        <button onClick={() => { clearToken(); onSignOut(); }} style={{ padding: "8px 20px", fontSize: 13, fontWeight: 600, background: "#2d5a1b", color: "#fff", border: "none", borderRadius: 7, cursor: "pointer" }}>
          Sign out
        </button>
      </div>
    </div>
  );

  const isGlobalAdmin    = user.admin;
  const isOpenfarmAdmin  = user.orgs?.some(o => o.slug === OPENFARM_SLUG);
  const canSeeAllOrgs    = isGlobalAdmin || isOpenfarmAdmin;
  const isOrgAdmin       = activeOrg?.is_org_admin || isGlobalAdmin;
  // Show Members tab when acting as org admin for a non-openfarm org
  const activeIsOpenfarm = activeOrg?.slug === OPENFARM_SLUG;
  const showMembers      = isOrgAdmin && (!!activeOrg && !activeIsOpenfarm);

  function signOut() { clearToken(); onSignOut(); }

  const tabs = [
    { key: "advanced",  label: t("advancedAnalysisTab"),   show: !!activeOrg },
    { key: "overview",  label: t("overviewTab"),           show: true },
    { key: "orgs",      label: t("organizationsTab"),      show: canSeeAllOrgs },
    { key: "members",   label: t("membersTab"),            show: showMembers },
    { key: "users",     label: t("usersAccessTab"),        show: isGlobalAdmin },
    { key: "analyses",  label: t("savedAnalysesTab"),      show: !!activeOrg },
    { key: "calendar",  label: t("plantingCalendarTab"),   show: !!activeOrg },
    { key: "activity",  label: t("fieldActivityTab"),      show: true },
    { key: "scout",     label: t("scoutReportsTab"),       show: true },
    { key: "charts",    label: t("chartsTab"),             show: !!activeOrg },
    { key: "recs",      label: t("recommendationsTab"),    show: !!activeOrg },
  ].filter(tab => tab.show);

  const LANGS = [{code:"en",label:"EN"},{code:"es",label:"ES"},{code:"fr",label:"FR"},{code:"zh",label:"中"},{code:"ar",label:"عر"},{code:"hi",label:"हि"},{code:"sw",label:"SW"}];

  return (
    <div style={{ height: "100dvh", display: "flex", flexDirection: "column", background: "#f0f6ea", fontFamily: "system-ui, sans-serif" }}>

      {/* ── Top bar ── */}
      <div style={{ background: "#1a2e1a", color: "#fff", padding: "0 16px", height: 52, flexShrink: 0, display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ fontSize: 16, fontWeight: 700 }}>OpenFarm</div>
        <div style={{ fontSize: 11, opacity: 0.5, borderLeft: "1px solid rgba(255,255,255,0.2)", paddingLeft: 12 }}>Dashboard</div>

        {/* Org switcher */}
        {user.orgs?.length > 1 && (
          <select value={activeOrg?.id || ""} onChange={e => {
            const o = user.orgs.find(x => x.id === Number(e.target.value));
            setActiveOrg(o); setTab("advanced");
          }} style={{ marginLeft: 8, padding: "4px 8px", fontSize: 12, background: "rgba(255,255,255,0.1)", color: "#fff", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 5, maxWidth: isMobile ? 130 : "none" }}>
            {user.orgs.map(o => <option key={o.id} value={o.id} style={{ color: "#000" }}>{o.name}</option>)}
          </select>
        )}
        {user.orgs?.length === 1 && (
          <div style={{ fontSize: 11, background: "rgba(255,255,255,0.1)", padding: "3px 8px", borderRadius: 5, opacity: 0.85, maxWidth: isMobile ? 130 : "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {user.orgs[0].name}
          </div>
        )}

        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
          {/* Desktop: full controls */}
          {!isMobile && <>
            {LANGS.map(l => (
              <button key={l.code} onClick={() => { i18n.changeLanguage(l.code); setLang(l.code); }} style={{
                padding:"3px 7px", fontSize:10, fontWeight:700, borderRadius:4, cursor:"pointer",
                background: lang===l.code ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.1)",
                color: lang===l.code ? "#1a2e1a" : "rgba(255,255,255,0.7)",
                border:"1px solid rgba(255,255,255,0.2)"
              }}>{l.label}</button>
            ))}
            <div style={{ width:1, height:20, background:"rgba(255,255,255,0.2)", margin:"0 2px" }} />
            <button onClick={() => onDisease && onDisease()} style={{ padding:"4px 10px", fontSize:11, fontWeight:600, background:"#3a7a1e", color:"#fff", border:"1px solid #7AB648", borderRadius:5, cursor:"pointer" }}>
              {t("diseaseDetector")}
            </button>
            <button onClick={() => printResult ? printFieldReport(printResult) : alert("Select a field analysis first.")}
              style={{ padding:"4px 10px", fontSize:11, fontWeight:600, background:"rgba(255,255,255,0.1)", color:"rgba(255,255,255,0.85)", border:"1px solid rgba(255,255,255,0.25)", borderRadius:5, cursor:"pointer" }}>
              {t("print")}
            </button>
            <div style={{ width:1, height:20, background:"rgba(255,255,255,0.2)", margin:"0 2px" }} />
            <span style={{ fontSize: 12, opacity: 0.65 }}>{user.email}</span>
            <button onClick={signOut} style={{ padding:"4px 12px", fontSize:11, fontWeight:600, background:"rgba(255,255,255,0.1)", color:"#fff", border:"1px solid rgba(255,255,255,0.2)", borderRadius:5, cursor:"pointer" }}>
              {t("signOut")}
            </button>
          </>}

          {/* Mobile: hamburger */}
          {isMobile && (
            <button onClick={() => setMenuOpen(o => !o)} style={{ background:"none", border:"none", color:"#fff", fontSize:22, cursor:"pointer", lineHeight:1, padding:"4px 2px" }}>
              {menuOpen ? "✕" : "☰"}
            </button>
          )}
        </div>
      </div>

      {/* ── Mobile slide-down menu ── */}
      {isMobile && menuOpen && (
        <div onClick={() => setMenuOpen(false)} style={{ position:"fixed", inset:0, zIndex:200, background:"rgba(0,0,0,0.45)" }}>
          <div onClick={e => e.stopPropagation()} style={{
            position:"absolute", top:52, right:0, width:260,
            background:"#1a2e1a", borderBottomLeftRadius:16,
            padding:"20px 16px 24px", display:"flex", flexDirection:"column", gap:14,
          }}>
            {/* Lang */}
            <div>
              <div style={{ fontSize:10, color:"rgba(255,255,255,0.5)", fontWeight:700, letterSpacing:1, marginBottom:8 }}>{t("languageLabel")}</div>
              <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                {LANGS.map(l => (
                  <button key={l.code} onClick={() => { i18n.changeLanguage(l.code); setLang(l.code); setMenuOpen(false); }} style={{
                    padding:"5px 10px", fontSize:12, fontWeight:700, borderRadius:6, cursor:"pointer",
                    background: lang===l.code ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.12)",
                    color: lang===l.code ? "#1a2e1a" : "rgba(255,255,255,0.8)",
                    border:"1px solid rgba(255,255,255,0.2)"
                  }}>{l.label}</button>
                ))}
              </div>
            </div>

            <div style={{ height:1, background:"rgba(255,255,255,0.1)" }} />

            {/* Actions */}
            <button onClick={() => { onDisease && onDisease(); setMenuOpen(false); }} style={{
              padding:"11px 14px", fontSize:13, fontWeight:600, background:"#3a7a1e",
              color:"#fff", border:"1px solid #7AB648", borderRadius:8, cursor:"pointer", textAlign:"left"
            }}>{t("diseaseDetector")}</button>

            <button onClick={() => { setMenuOpen(false); printResult ? printFieldReport(printResult) : alert("Select a field analysis first."); }} style={{
              padding:"11px 14px", fontSize:13, fontWeight:600,
              background:"rgba(255,255,255,0.1)", color:"rgba(255,255,255,0.9)",
              border:"1px solid rgba(255,255,255,0.2)", borderRadius:8, cursor:"pointer", textAlign:"left"
            }}>{t("print")}</button>

            <div style={{ height:1, background:"rgba(255,255,255,0.1)" }} />

            <div style={{ fontSize:11, color:"rgba(255,255,255,0.5)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{user.email}</div>
            <button onClick={() => { setMenuOpen(false); signOut(); }} style={{
              padding:"11px 14px", fontSize:13, fontWeight:600,
              background:"rgba(255,255,255,0.08)", color:"#ff9a9a",
              border:"1px solid rgba(255,100,100,0.3)", borderRadius:8, cursor:"pointer", textAlign:"left"
            }}>{t("signOut")}</button>
          </div>
        </div>
      )}

      {/* ── Desktop: horizontal tab nav ── */}
      {!isMobile && (
        <div style={{ background: "#fff", borderBottom: "1px solid #dde8d0", padding: "0 24px", display: "flex", gap: 2, flexShrink: 0, overflowX: "auto" }}>
          {tabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              padding: "12px 16px", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap",
              border: "none", borderBottom: tab === t.key ? "2px solid #2d5a1b" : "2px solid transparent",
              background: "transparent", color: tab === t.key ? "#2d5a1b" : "#7a9a6a",
              cursor: "pointer", flexShrink: 0,
            }}>{t.label}</button>
          ))}
        </div>
      )}

      {/* ── Content ── */}
      <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column", paddingBottom: isMobile ? 64 : 0 }}>
        {tab === "advanced"
          ? <AdvancedAnalysisTab org={activeOrg} lang={lang} />
          : <div style={{ flex: 1, overflowY: "auto" }}><div style={{ padding: isMobile ? 16 : 24, maxWidth: 1100, margin: "0 auto" }}>
              {tab === "overview"  && <OverviewTab user={user} activeOrg={activeOrg} isGlobalAdmin={isGlobalAdmin} isOrgAdmin={isOrgAdmin} />}
              {tab === "orgs"      && <OrgsTab isGlobalAdmin={isGlobalAdmin} />}
              {tab === "members"   && <OrgMembersTab org={activeOrg} user={user} />}
              {tab === "users"     && <UsersTab />}
              {tab === "analyses"  && <SavedAnalysesTab org={activeOrg} lang={lang} />}
              {tab === "calendar"  && <PlantingCalendarTab org={activeOrg} lang={lang} />}
              {tab === "activity"  && <ActivityTab org={activeOrg} lang={lang} />}
              {tab === "scout"     && <ScoutTab org={activeOrg} />}
              {tab === "charts"    && <ChartsTab org={activeOrg} lang={lang} />}
              {tab === "recs"      && <RecommendationsTab org={activeOrg} lang={lang} />}
            </div></div>
        }
      </div>

      {/* ── Mobile: bottom tab bar ── */}
      {isMobile && (
        <div style={{
          position:"fixed", bottom:0, left:0, right:0, zIndex:100,
          background:"#fff", borderTop:"1px solid #dde8d0",
          display:"flex", height:56,
        }}>
          {tabs.slice(0, 5).map(t => (
            <button key={t.key} onClick={() => { setTab(t.key); setMoreOpen(false); }} style={{
              flex:1, display:"flex", alignItems:"center", justifyContent:"center",
              border:"none", background:"none",
              color: tab===t.key ? "#2d5a1b" : "#888", cursor:"pointer",
              borderTop: tab===t.key ? "2px solid #2d5a1b" : "2px solid transparent",
              fontSize:11, fontWeight: tab===t.key ? 700 : 500,
              padding:"0 4px", textAlign:"center",
              whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
            }}>
              {t.label.split(" ")[0]}
            </button>
          ))}
          {tabs.length > 5 && (
            <button onClick={() => setMoreOpen(o => !o)} style={{
              flex:1, display:"flex", alignItems:"center", justifyContent:"center",
              border:"none", borderTop: moreOpen ? "2px solid #2d5a1b" : "2px solid transparent",
              background: moreOpen ? "#f0f6ea" : "none",
              color: moreOpen ? "#2d5a1b" : "#888", cursor:"pointer", fontSize:11, fontWeight:600,
            }}>
              {t("moreBtn")}
            </button>
          )}
        </div>
      )}

      {/* ── Mobile: "More" tabs overlay ── */}
      {isMobile && moreOpen && tabs.length > 5 && (
        <div onClick={() => setMoreOpen(false)} style={{ position:"fixed", inset:0, zIndex:99, background:"rgba(0,0,0,0.3)" }}>
          <div onClick={e => e.stopPropagation()} style={{
            position:"absolute", bottom:56, left:0, right:0,
            background:"#fff", borderTop:"1px solid #dde8d0",
            borderTopLeftRadius:16, borderTopRightRadius:16,
            padding:"16px 16px 8px",
          }}>
            <div style={{ fontSize:11, color:"#aaa", fontWeight:700, letterSpacing:1, marginBottom:10 }}>MORE</div>
            {tabs.slice(5).map(t => (
              <button key={t.key} onClick={() => { setTab(t.key); setMoreOpen(false); }} style={{
                display:"flex", alignItems:"center", width:"100%",
                padding:"13px 8px", border:"none", background:"none",
                borderBottom:"1px solid #f0f6ea", cursor:"pointer",
                color: tab===t.key ? "#2d5a1b" : "#333",
                fontWeight: tab===t.key ? 700 : 400, fontSize:14, textAlign:"left",
              }}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Placeholder tab components (to be built out) ──────────────────────────────
function StatCard({ label, value, sub, color = "#2d5a1b" }) {
  const len = String(value ?? "").length;
  const fs = len > 10 ? 14 : len > 6 ? 18 : len > 4 ? 24 : 32;
  return (
    <div style={{ background: "#fff", borderRadius: 10, padding: "16px 20px", border: "1px solid #e2ead8", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#7a9a6a", marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: fs, fontWeight: 700, color, lineHeight: 1.2 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "#999", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function OverviewTab({ user, activeOrg, isGlobalAdmin, isOrgAdmin }) {
  const [stats,   setStats]   = useState(null);
  const [metrics, setMetrics] = useState(null);
  const headers = { Authorization: `Bearer ${getToken()}` };

  const showGlobalStats = isGlobalAdmin && activeOrg?.slug === "openfarm";

  const load = useCallback(() => {
    if (showGlobalStats) {
      axios.get(`${API}/auth/admin/stats`, { headers })
        .then(r => setStats(r.data)).catch(() => {});
      // Load server traffic metrics (no secret needed — already behind auth)
      axios.get(`${API}/queue-status`).then(r => {
        // Also fetch full metrics if available
        axios.get(`${API}/metrics`, { headers: { ...headers, "x-metrics-secret": "" } })
          .then(m => setMetrics(m.data))
          .catch(() => setMetrics(r.data));  // fall back to queue-status data
      }).catch(() => {});
    } else if (activeOrg) {
      axios.get(`${API}/auth/org/${activeOrg.id}/stats`, { headers })
        .then(r => setStats(r.data)).catch(() => {});
    }
  }, [showGlobalStats, activeOrg?.id]);

  useEffect(() => { load(); }, [load]);

  // Poll every 30s so stats stay live as members are approved
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: "#1a2e1a", margin: 0 }}>Overview</h2>
        {activeOrg && <span style={{ fontSize: 12, color: "#7a9a6a", fontWeight: 600 }}>{activeOrg.name}</span>}
      </div>

      {/* Stat cards — skeleton until loaded */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 16, marginBottom: 28 }}>
        {!stats ? (
          Array.from({ length: showGlobalStats ? 4 : isOrgAdmin ? 3 : 1 }, (_, i) => (
            <div key={i} style={{ background: "#fff", borderRadius: 10, padding: "18px 20px", border: "1px solid #e2ead8" }}>
              <Sk w="60%" h={11} r={4} mb={10} />
              <Sk w="35%" h={26} r={6} />
            </div>
          ))
        ) : showGlobalStats ? <>
          <StatCard label="Authorized Users"  value={stats?.authorized_users ?? "–"} />
          <StatCard label="Pending Requests"  value={stats?.pending_requests ?? "–"} color={stats?.pending_requests > 0 ? "#b45309" : "#2d5a1b"} />
          <StatCard label="Analyses Today"    value={stats?.analyses_today   ?? "–"} />
          <StatCard label="Scout Reports"     value={stats?.scout_reports     ?? "–"} />
        </> : isOrgAdmin ? <>
          <StatCard label="Members"           value={stats?.members           ?? "–"} />
          <StatCard label="Pending Requests"  value={stats?.pending_requests  ?? "–"} color={stats?.pending_requests > 0 ? "#b45309" : "#2d5a1b"} />
          <StatCard label="Saved Analyses"    value={stats?.saved_analyses    ?? "–"} />
        </> : <>
          <StatCard label="Saved Analyses"    value={stats?.saved_analyses    ?? "–"} />
        </>}
      </div>

      {isOrgAdmin && stats?.pending_requests > 0 && (
        <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, padding: "14px 20px", marginBottom: 20, fontSize: 13, color: "#92400e" }}>
          <strong>{stats.pending_requests} pending request{stats.pending_requests > 1 ? "s" : ""}</strong> — go to the <strong>Members</strong> tab to review.
        </div>
      )}

      {/* Server traffic panel — global admin only */}
      {showGlobalStats && (
        <div style={{ background: "#fff", border: "1px solid #e2ead8", borderRadius: 12, padding: "20px 24px", marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#1a2e1a", marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
            Server Traffic
            <span style={{ fontSize: 10, background: "#f0f6ea", color: "#5a7a4a", borderRadius: 5, padding: "2px 7px", fontWeight: 600 }}>live</span>
          </div>
          {!metrics ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 12 }}>
              {Array.from({ length: 5 }, (_, i) => (
                <div key={i} style={{ background: "#f8faf6", borderRadius: 8, padding: "12px 14px" }}>
                  <Sk w="55%" h={10} r={4} mb={8} />
                  <Sk w="35%" h={20} r={5} />
                </div>
              ))}
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 12 }}>
              {[
                { label: "Req / min",     value: metrics.requests_per_minute ?? metrics.rpm ?? "–" },
                { label: "Active analyses", value: metrics.active_gee_analyses ?? metrics.active ?? "–" },
                { label: "Queued",        value: metrics.queue_depth ?? metrics.queued ?? "–" },
                { label: "Total requests", value: metrics.counters?.total_requests ?? "–" },
                { label: "Errors",        value: metrics.counters?.total_errors ?? "–",
                  color: (metrics.counters?.total_errors > 0) ? "#b45309" : "#2d5a1b" },
                { label: "Rate limited",  value: metrics.counters?.rate_limited ?? "–" },
                { label: "Queue timeouts", value: metrics.counters?.queue_timeouts ?? "–" },
                { label: "Server uptime", value: metrics.uptime_hours != null ? `${metrics.uptime_hours}h` : "–" },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ background: "#f8faf6", borderRadius: 8, padding: "12px 14px" }}>
                  <div style={{ fontSize: 10, color: "#7a9a6a", fontWeight: 600, marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: color || "#1a2e1a" }}>{value}</div>
                </div>
              ))}
            </div>
          )}
          {metrics?.ee_quota_estimate && (
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid #f0f0e8" }}>
              <div style={{ fontSize: 11, color: "#7a9a6a", fontWeight: 600, marginBottom: 6 }}>Google Earth Engine Quota (estimated)</div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ flex: 1, height: 8, background: "#e2ead8", borderRadius: 4, overflow: "hidden" }}>
                  <div style={{
                    height: "100%", borderRadius: 4,
                    width: `${metrics.ee_quota_estimate.utilization_pct}%`,
                    background: metrics.ee_quota_estimate.utilization_pct > 80 ? "#dc2626"
                      : metrics.ee_quota_estimate.utilization_pct > 50 ? "#f59e0b" : "#16a34a",
                  }} />
                </div>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#1a2e1a", minWidth: 38 }}>
                  {metrics.ee_quota_estimate.utilization_pct}%
                </span>
                <span style={{ fontSize: 11, color: "#9ca3af" }}>
                  ~{metrics.ee_quota_estimate.projected_daily} / {metrics.ee_quota_estimate.daily_budget_approx} req today
                </span>
              </div>
              <div style={{ fontSize: 10, color: "#bbb", marginTop: 4 }}>{metrics.ee_quota_estimate.note}</div>
            </div>
          )}
        </div>
      )}

      {activeOrg && <WeatherForecastPanel org={activeOrg} />}
      {activeOrg && <div style={{ marginTop: 24 }}><WeatherAlertsPanel org={activeOrg} /></div>}
    </div>
  );
}

// ── Weather Forecast (Open-Meteo, no key required) ────────────────────────────
const WMO_LABEL = {
  0:"Clear", 1:"Mostly clear", 2:"Partly cloudy", 3:"Overcast",
  45:"Fog", 48:"Icy fog",
  51:"Light drizzle", 53:"Drizzle", 55:"Heavy drizzle",
  61:"Light rain", 63:"Rain", 65:"Heavy rain",
  71:"Light snow", 73:"Snow", 75:"Heavy snow", 77:"Snow grains",
  80:"Rain showers", 81:"Showers", 82:"Heavy showers",
  85:"Snow showers", 86:"Heavy snow showers",
  95:"Thunderstorm", 96:"Thunderstorm + hail", 99:"Severe thunderstorm",
};
const WMO_ICON = {
  0:"Clear", 1:"Clear", 2:"Cloudy", 3:"Overcast",
  45:"Fog", 48:"Fog",
  51:"Drizzle", 53:"Drizzle", 55:"Drizzle",
  61:"Rain", 63:"Rain", 65:"Rain",
  71:"Snow", 73:"Snow", 75:"Snow", 77:"Snow",
  80:"Showers", 81:"Showers", 82:"Showers",
  85:"Snow showers", 86:"Snow showers",
  95:"Thunder", 96:"Thunder", 99:"Thunder",
};

function WeatherForecastPanel({ org }) {
  const [analyses, setAnalyses] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(false);
  const headers = { Authorization: `Bearer ${getToken()}` };

  useEffect(() => {
    if (!org) return;
    axios.get(`${API}/analyses/org/${org.id}`, { headers })
      .then(r => {
        setAnalyses(r.data || []);
        if (r.data?.length) setSelectedId(r.data[0].id);
      }).catch(() => {});
  }, [org]);

  useEffect(() => {
    if (!selectedId || !analyses.length) return;
    const a = analyses.find(x => x.id === selectedId);
    if (!a?.polygon) return;
    const p = a.polygon;
    const coords = Array.isArray(p) ? p : (p?.coordinates?.[0] || []);
    if (!coords.length) return;
    const lat = coords.reduce((s, c) => s + c[1], 0) / coords.length;
    const lon = coords.reduce((s, c) => s + c[0], 0) / coords.length;
    setLoading(true); setForecast(null);
    fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat.toFixed(4)}&longitude=${lon.toFixed(4)}&daily=temperature_2m_max,temperature_2m_min,precipitation_sum,weathercode,windspeed_10m_max&forecast_days=7&timezone=auto`)
      .then(r => r.json())
      .then(d => { setForecast({ ...d.daily, fieldName: a.name }); setLoading(false); })
      .catch(() => setLoading(false));
  }, [selectedId, analyses.length]); // eslint-disable-line

  const days = forecast?.time || [];

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#1a2e1a" }}>7-Day Forecast</div>
        {analyses.length > 1 && (
          <select value={selectedId || ""} onChange={e => setSelectedId(Number(e.target.value))}
            style={{ ...S.input, width: 200, height: 30, paddingTop: 0, paddingBottom: 0, fontSize: 12 }}>
            {analyses.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        )}
        {forecast && <span style={{ fontSize: 11, color: "#aaa" }}>{forecast.fieldName}</span>}
      </div>

      {loading && <div style={{ paddingTop: 8 }}><Sk w="60%" h={12} r={4} mb={6} /><Sk w="80%" h={12} r={4} mb={6} /><Sk w="45%" h={12} r={4} /></div>}
      {!loading && !days.length && analyses.length === 0 && (
        <div style={{ fontSize: 12, color: "#aaa" }}>Save a field to see its weather forecast.</div>
      )}

      {days.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 8 }}>
          {days.map((date, i) => {
            const code   = forecast.weathercode?.[i] ?? 0;
            const tmax   = forecast.temperature_2m_max?.[i];
            const tmin   = forecast.temperature_2m_min?.[i];
            const rain   = forecast.precipitation_sum?.[i];
            const wind   = forecast.windspeed_10m_max?.[i];
            const isToday = i === 0;
            const d = new Date(date + "T12:00:00");
            const dayLabel = isToday ? "Today" : d.toLocaleDateString(undefined, { weekday: "short" });
            const dateLabel = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
            return (
              <div key={date} style={{ background: isToday ? "#f0fdf4" : "#fff",
                border: `1px solid ${isToday ? "#bbf7d0" : "#e2ead8"}`,
                borderRadius: 10, padding: "10px 8px", textAlign: "center" }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: isToday ? "#15803d" : "#7a9a6a", marginBottom: 2 }}>{dayLabel}</div>
                <div style={{ fontSize: 10, color: "#aaa", marginBottom: 6 }}>{dateLabel}</div>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#374151", marginBottom: 6, lineHeight: 1.3 }}>{WMO_LABEL[code] || WMO_ICON[code] || "—"}</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#dc2626" }}>{tmax != null ? `${Math.round(tmax)}°` : "—"}</div>
                <div style={{ fontSize: 12, color: "#3b82f6" }}>{tmin != null ? `${Math.round(tmin)}°` : "—"}</div>
                {rain > 0 && <div style={{ fontSize: 10, color: "#0369a1", marginTop: 4 }}>{rain.toFixed(1)} mm</div>}
                {wind != null && <div style={{ fontSize: 10, color: "#6b7280", marginTop: 2 }}>{Math.round(wind)} km/h</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const ALERT_CFG = {
  heat_stress: { label: "Heat Stress",  bg: "#fee2e2", color: "#991b1b", border: "#fca5a5" },
  cold_stress: { label: "Cold Stress",  bg: "#dbeafe", color: "#1e40af", border: "#93c5fd" },
  frost:       { label: "Frost",        bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" },
  drought:     { label: "Drought",      bg: "#fef9c3", color: "#854d0e", border: "#fde047" },
};

function WeatherAlertsPanel({ org }) {
  const [alerts, setAlerts]   = useState(null);
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${getToken()}` };

  useEffect(() => {
    if (!org) return;
    axios.get(`${API}/weather-alerts/org/${org.id}`, { headers })
      .then(r => { setAlerts(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [org]);

  if (loading) return <div style={{ marginTop: 8 }}><Sk w="70%" h={12} r={4} mb={6} /><Sk w="50%" h={12} r={4} /></div>;
  if (!alerts?.alerts?.length) return (
    <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10,
      padding: "14px 18px", fontSize: 13, color: "#15803d" }}>
      No weather alerts for your fields in the last 30 days.
    </div>
  );

  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#1a2e1a", marginBottom: 10 }}>
        Weather Alerts <span style={{ fontSize: 11, fontWeight: 400, color: "#aaa" }}>— last 30 days</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {alerts.alerts.map((a, i) => {
          const cfg = ALERT_CFG[a.type] || { label: a.type, bg: "#f3f4f6", color: "#374151", border: "#d1d5db" };
          return (
            <div key={i} style={{ background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: 8,
              padding: "10px 14px", display: "flex", gap: 12, alignItems: "flex-start" }}>
              <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 8,
                background: cfg.color, color: "#fff", flexShrink: 0, marginTop: 1 }}>
                {cfg.label}
              </span>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: cfg.color, marginBottom: 2 }}>{a.field}</div>
                <div style={{ fontSize: 12, color: "#374151" }}>{a.message}</div>
              </div>
              {a.severity === "high" && (
                <span style={{ marginLeft: "auto", fontSize: 10, fontWeight: 700, color: "#991b1b",
                  background: "#fee2e2", padding: "1px 6px", borderRadius: 6, flexShrink: 0 }}>HIGH</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OrgSettingsPanel({ org, onSaved }) {
  const { t } = useTranslation();
  const headers = { Authorization: `Bearer ${getToken()}` };
  const [vals, setVals] = useState({
    max_users:          org.max_users          ?? "",
    acreage_limit_ha:   org.acreage_limit_ha   ?? "",
    auto_refresh_hours: org.auto_refresh_hours ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [msg,    setMsg]    = useState(null);

  async function save() {
    setSaving(true);
    setMsg(null);
    const patch = { org_id: org.id };
    patch.max_users          = vals.max_users          === "" ? null : Number(vals.max_users);
    patch.acreage_limit_ha   = vals.acreage_limit_ha   === "" ? null : Number(vals.acreage_limit_ha);
    patch.auto_refresh_hours = vals.auto_refresh_hours === "" ? null : Number(vals.auto_refresh_hours);
    try {
      await axios.patch(`${API}/auth/admin/org-settings`, patch, { headers });
      setMsg("✓ " + t("orgSettingsSaved"));
      onSaved();
    } catch (e) {
      setMsg("Error: " + (e?.response?.data?.error || e.message));
    }
    setSaving(false);
  }

  const fieldStyle = { display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 120 };
  const labelStyle = { fontSize: 11, fontWeight: 600, color: "#5a8a3a" };
  const numInput   = { ...S.input, marginTop: 0, padding: "7px 10px", fontSize: 12 };

  return (
    <div style={{ background: "#f0f6ea", border: "1px solid #c8ddb0", borderRadius: 8, padding: "14px 16px", marginTop: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#2d5a1b", marginBottom: 12 }}>{t("orgLimitsTitle")}</div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
        <div style={fieldStyle}>
          <label style={labelStyle}>{t("orgMaxUsers")} <span style={{ color: "#aaa", fontWeight: 400 }}>({t("emptyUnlimited")})</span></label>
          <input type="number" min="1" step="1" placeholder="∞" value={vals.max_users}
            onChange={e => setVals(v => ({ ...v, max_users: e.target.value }))} style={numInput} />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>{t("orgAcreageLimit")} ha <span style={{ color: "#aaa", fontWeight: 400 }}>({t("emptyUnlimited")})</span></label>
          <input type="number" min="0.1" step="0.1" placeholder="∞" value={vals.acreage_limit_ha}
            onChange={e => setVals(v => ({ ...v, acreage_limit_ha: e.target.value }))} style={numInput} />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>{t("orgAutoRefresh")} (h) <span style={{ color: "#aaa", fontWeight: 400 }}>({t("emptyDisabled")})</span></label>
          <input type="number" min="1" step="1" placeholder="—" value={vals.auto_refresh_hours}
            onChange={e => setVals(v => ({ ...v, auto_refresh_hours: e.target.value }))} style={numInput} />
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <button onClick={save} disabled={saving}
          style={{ ...S.btn, width: "auto", padding: "8px 18px", marginTop: 0, fontSize: 12, opacity: saving ? 0.6 : 1 }}>
          {saving ? t("savingBtn") : t("saveBtn")}
        </button>
        {msg && <span style={{ fontSize: 12, color: msg.startsWith("✓") ? "#2d5a1b" : "#c0392b" }}>{msg}</span>}
      </div>
    </div>
  );
}

function OrgsTab({ isGlobalAdmin }) {
  const { t } = useTranslation();
  const [orgs,        setOrgs]        = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [msg,         setMsg]         = useState(null);
  const [addEmail,    setAddEmail]    = useState({});
  const [openSettings, setOpenSettings] = useState({});
  const headers = { Authorization: `Bearer ${getToken()}` };

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/auth/admin/orgs`, { headers })
      .then(r => { setOrgs(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function addMember(orgId, orgName) {
    const email = addEmail[orgId]?.trim().toLowerCase();
    if (!email) return;
    await axios.post(`${API}/auth/admin/add-org-member`, { email, org_id: orgId }, { headers });
    setMsg(`✓ ${email} added to ${orgName}`);
    setAddEmail(prev => ({ ...prev, [orgId]: "" }));
    load();
  }

  async function removeMember(email, orgId, orgName) {
    if (!window.confirm(`Remove ${email} from ${orgName}?`)) return;
    await axios.post(`${API}/auth/admin/remove-org-member`, { email, org_id: orgId }, { headers });
    setMsg(`✓ ${email} removed from ${orgName}`);
    load();
  }

  const rowStyle = { display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #f0f6ea" };

  const [newOrgName, setNewOrgName] = useState("");
  const [creating, setCreating] = useState(false);

  async function createOrg() {
    const name = newOrgName.trim();
    if (!name) return;
    setCreating(true);
    try {
      await axios.post(`${API}/auth/admin/create-org`, { name }, { headers });
      setMsg(`✓ Organization "${name}" created`);
      setNewOrgName("");
      load();
    } catch (e) {
      setMsg(`Error: ${e?.response?.data?.error || e.message}`);
    }
    setCreating(false);
  }

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: "#1a2e1a", marginBottom: 20 }}>{t("organizationsTab")}</h2>
      {msg && <div style={{ ...S.ok, marginBottom: 16 }}>{msg}</div>}
      {/* Create new org */}
      <div style={{ background: "#f8faf6", border: "1px solid #e2ead8", borderRadius: 10, padding: "16px 20px", marginBottom: 24, display: "flex", gap: 10, alignItems: "center" }}>
        <input
          type="text"
          placeholder={t("newOrgNamePlaceholder")}
          value={newOrgName}
          onChange={e => setNewOrgName(e.target.value)}
          onKeyDown={e => e.key === "Enter" && createOrg()}
          style={{ ...S.input, flex: 1, marginTop: 0 }}
        />
        <button onClick={createOrg} disabled={creating || !newOrgName.trim()}
          style={{ ...S.btn, width: "auto", padding: "10px 18px", marginTop: 0, opacity: creating || !newOrgName.trim() ? 0.6 : 1 }}>
          {creating ? t("creatingBtn") : t("createOrg")}
        </button>
      </div>
      {loading ? <><SkCard lines={3} mb={12} /><SkCard lines={3} mb={12} /></> : orgs.map(org => (
        <div key={org.id} style={{ background: "#fff", borderRadius: 10, padding: "20px 24px", border: "1px solid #e2ead8", marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#1a2e1a" }}>{org.name}</div>
            <div style={{ fontSize: 11, background: "#f0f6ea", color: "#5a8a3a", padding: "2px 8px", borderRadius: 10, fontWeight: 600 }}>
              {org.member_count}{org.max_users != null ? `/${org.max_users}` : ""} {t("membersLabel")}
            </div>
            {org.acreage_limit_ha != null && (
              <div style={{ fontSize: 11, background: "#fff7ed", color: "#c05a00", padding: "2px 8px", borderRadius: 10, fontWeight: 600 }}>
                ≤ {org.acreage_limit_ha} ha
              </div>
            )}
            {org.auto_refresh_hours != null && (
              <div style={{ fontSize: 11, background: "#f0f4ff", color: "#3b4ea6", padding: "2px 8px", borderRadius: 10, fontWeight: 600 }}>
                ↻ {org.auto_refresh_hours}h
              </div>
            )}
            {isGlobalAdmin && (
              <button onClick={() => setOpenSettings(s => ({ ...s, [org.id]: !s[org.id] }))}
                style={{ marginLeft: "auto", padding: "4px 10px", fontSize: 11, fontWeight: 600, background: openSettings[org.id] ? "#2d5a1b" : "#fff", color: openSettings[org.id] ? "#fff" : "#2d5a1b", border: "1px solid #b8d4a0", borderRadius: 6, cursor: "pointer" }}>
                ⚙ {t("orgSettingsBtn")}
              </button>
            )}
          </div>
          {org.invite_token && (
            <div style={{ background: "#f8faf6", border: "1px solid #e2ead8", borderRadius: 7, padding: "8px 12px", marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 11, color: "#7a9a6a", fontWeight: 600 }}>{t("inviteLink")}</span>
              <code style={{ fontSize: 11, color: "#1a2e1a", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {`${window.location.origin}?invite=${org.invite_token}`}
              </code>
              <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}?invite=${org.invite_token}`); setMsg("✓ Invite link copied!"); }}
                style={{ padding: "3px 10px", fontSize: 11, fontWeight: 600, background: "#2d5a1b", color: "#fff", border: "none", borderRadius: 5, cursor: "pointer", whiteSpace: "nowrap" }}>
                {t("copyBtn")}
              </button>
            </div>
          )}

          {/* Org limits settings panel */}
          {isGlobalAdmin && openSettings[org.id] && (
            <OrgSettingsPanel org={org} onSaved={load} />
          )}

          {/* Member list */}
          {org.members?.map(m => (
            <div key={m.email} style={rowStyle}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{m.email}
                  {m.is_org_admin && <span style={{ marginLeft: 8, fontSize: 10, background: "#2d5a1b", color: "#fff", borderRadius: 4, padding: "1px 6px" }}>{t("adminBadge")}</span>}
                </div>
                <div style={{ fontSize: 11, color: "#999" }}>Added {new Date(m.approved_at).toLocaleDateString()}</div>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                {isGlobalAdmin && (
                  <button onClick={async () => {
                    await axios.post(`${API}/auth/admin/set-org-admin`,
                      { email: m.email, org_id: org.id, is_org_admin: !m.is_org_admin },
                      { headers: { Authorization: `Bearer ${getToken()}` } });
                    setMsg(`✓ ${m.email} ${m.is_org_admin ? "demoted" : "made org admin"}`);
                    load();
                  }} style={{ padding: "4px 8px", fontSize: 10, fontWeight: 600, background: m.is_org_admin ? "#fff7ed" : "#f0fdf4", color: m.is_org_admin ? "#c0392b" : "#2d5a1b", border: `1px solid ${m.is_org_admin ? "#fecaca" : "#bbf7d0"}`, borderRadius: 5, cursor: "pointer" }}>
                    {m.is_org_admin ? t("demoteBtn") : t("makeAdminBtn")}
                  </button>
                )}
                <button onClick={() => removeMember(m.email, org.id, org.name)}
                  style={{ padding: "4px 10px", fontSize: 11, fontWeight: 600, background: "#fff", color: "#c0392b", border: "1px solid #fecaca", borderRadius: 5, cursor: "pointer" }}>
                  {t("removeBtn")}
                </button>
              </div>
            </div>
          ))}

          {/* Add member */}
          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <input type="email" placeholder={t("addMemberPlaceholder")}
              value={addEmail[org.id] || ""}
              onChange={e => setAddEmail(prev => ({ ...prev, [org.id]: e.target.value }))}
              style={{ ...S.input, flex: 1, marginTop: 0 }}
              onKeyDown={e => e.key === "Enter" && addMember(org.id, org.name)}
            />
            <button onClick={() => addMember(org.id, org.name)}
              style={{ ...S.btn, width: "auto", padding: "10px 16px", marginTop: 0 }}>
              {t("addBtn")}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Org Members Tab (for org admins who are NOT global admin) ─────────────────
function OrgMembersTab({ org, user }) {
  const [members,   setMembers]  = useState([]);
  const [pending,   setPending]  = useState([]);
  const [loading,   setLoading]  = useState(true);
  const [inviteLink, setInviteLink] = useState("");
  const [msg,       setMsg]      = useState(null);
  const [addEmail,  setAddEmail] = useState("");
  const [adding,    setAdding]   = useState(false);
  const headers = { Authorization: `Bearer ${getToken()}` };

  const load = useCallback(() => {
    if (!org) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/auth/org/${org.id}/members`,  { headers }),
      axios.get(`${API}/auth/org/${org.id}/pending`,  { headers }),
      axios.get(`${API}/auth/org/${org.id}/invite-link`, { headers }),
    ]).then(([m, p, inv]) => {
      setMembers(m.data);
      setPending(p.data);
      setInviteLink(`${window.location.origin}?invite=${inv.data.invite_token}`);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [org]);

  useEffect(() => { load(); }, [load]);

  async function approveUser(email, approve_token) {
    await axios.post(`${API}/auth/org/${org.id}/approve`,
      { email, approve_token, org_id: org.id }, { headers });
    setMsg(`✓ ${email} approved`);
    load();
  }

  async function denyUser(email) {
    if (!window.confirm(`Deny access for ${email}?`)) return;
    await axios.post(`${API}/auth/org/${org.id}/deny`, { email }, { headers });
    setMsg(`✗ ${email} denied`);
    load();
  }

  async function removeMember(email) {
    if (!window.confirm(`Remove ${email} from this organization?`)) return;
    await axios.post(`${API}/auth/org/${org.id}/remove-member`, { email }, { headers });
    setMsg(`✓ ${email} removed`);
    load();
  }

  async function toggleAdmin(email, currently) {
    await axios.post(`${API}/auth/org/${org.id}/set-admin`, { email, is_org_admin: !currently }, { headers });
    setMsg(`✓ ${email} ${currently ? "demoted" : "made admin"}`);
    load();
  }

  async function addMemberByEmail() {
    const email = addEmail.trim().toLowerCase();
    if (!email) return;
    setAdding(true);
    try {
      await axios.post(`${API}/auth/org/${org.id}/add-member`, { email }, { headers });
      setMsg(`✓ ${email} added`);
      setAddEmail("");
      load();
    } catch (e) {
      setMsg(`Error: ${e?.response?.data?.error || e.message}`);
    }
    setAdding(false);
  }

  const rowStyle = { display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center", padding: "9px 0", borderBottom: "1px solid #f0f6ea" };

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: "#1a2e1a", marginBottom: 20 }}>{org?.name} — Members</h2>
      {msg && <div style={{ ...S.ok, marginBottom: 16 }}>{msg}</div>}

      {/* Invite link */}
      {inviteLink && (
        <div style={{ background: "#fff", borderRadius: 10, padding: "16px 20px", border: "1px solid #e2ead8", marginBottom: 20, display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#4a6a3a" }}>Invite link:</span>
          <code style={{ fontSize: 11, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#1a2e1a" }}>{inviteLink}</code>
          <button onClick={() => { navigator.clipboard.writeText(inviteLink); setMsg("✓ Copied!"); }}
            style={{ padding: "5px 12px", fontSize: 11, fontWeight: 600, background: "#2d5a1b", color: "#fff", border: "none", borderRadius: 5, cursor: "pointer" }}>
            Copy
          </button>
        </div>
      )}

      {/* Pending */}
      {pending.length > 0 && (
        <div style={{ background: "#fffbeb", borderRadius: 10, padding: "16px 20px", border: "1px solid #fde68a", marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#92400e", marginBottom: 10 }}>⚠ Pending ({pending.length})</div>
          {pending.map(r => (
            <div key={r.email} style={rowStyle}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{r.email}</div>
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={() => approveUser(r.email, r.approve_token)}
                  style={{ padding: "5px 12px", fontSize: 12, fontWeight: 600, background: "#2d5a1b", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" }}>
                  Approve
                </button>
                <button onClick={() => denyUser(r.email)}
                  style={{ padding: "5px 12px", fontSize: 12, fontWeight: 600, background: "#fff", color: "#c0392b", border: "1px solid #fecaca", borderRadius: 6, cursor: "pointer" }}>
                  Deny
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Members */}
      <div style={{ background: "#fff", borderRadius: 10, padding: "16px 20px", border: "1px solid #e2ead8" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#1a2e1a", marginBottom: 10 }}>Members ({members.length})</div>
        {loading ? <><SkRow cols={3} mb={10} /><SkRow cols={3} mb={10} /><SkRow cols={3} mb={0} /></> : members.map(m => (
          <div key={m.email} style={rowStyle}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{m.email}
                {m.is_org_admin && <span style={{ marginLeft: 8, fontSize: 10, background: "#2d5a1b", color: "#fff", borderRadius: 4, padding: "1px 6px" }}>Admin</span>}
              </div>
              <div style={{ fontSize: 11, color: "#999" }}>Joined {new Date(m.approved_at).toLocaleDateString()}</div>
            </div>
            {m.email !== user?.email && (
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={() => toggleAdmin(m.email, m.is_org_admin)}
                  style={{ padding: "4px 8px", fontSize: 10, fontWeight: 600, background: m.is_org_admin ? "#fff7ed" : "#f0fdf4", color: m.is_org_admin ? "#c0392b" : "#2d5a1b", border: `1px solid ${m.is_org_admin ? "#fecaca" : "#bbf7d0"}`, borderRadius: 5, cursor: "pointer" }}>
                  {m.is_org_admin ? "Demote" : "Make admin"}
                </button>
                <button onClick={() => removeMember(m.email)}
                  style={{ padding: "4px 10px", fontSize: 11, fontWeight: 600, background: "#fff", color: "#c0392b", border: "1px solid #fecaca", borderRadius: 5, cursor: "pointer" }}>
                  Remove
                </button>
              </div>
            )}
          </div>
        ))}

        {/* Add member by email */}
        <div style={{ display: "flex", gap: 8, marginTop: 16, paddingTop: 14, borderTop: "1px solid #f0f6ea" }}>
          <input type="email" placeholder="Add member by email…"
            value={addEmail}
            onChange={e => setAddEmail(e.target.value)}
            onKeyDown={e => e.key === "Enter" && addMemberByEmail()}
            style={{ ...S.input, flex: 1, marginTop: 0 }}
          />
          <button onClick={addMemberByEmail} disabled={adding || !addEmail.trim()}
            style={{ ...S.btn, width: "auto", padding: "10px 16px", marginTop: 0, opacity: adding || !addEmail.trim() ? 0.6 : 1 }}>
            {adding ? "Adding…" : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Planting Calendar Tab ──────────────────────────────────────────────────────
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const CROPS_LIST = [
  "Wheat","Maize","Rice","Soybean","Potato","Tomato","Sugarcane","Sorghum",
  "Millet","Beans","Sweet Potato","Cassava","Coffee","Tea","Banana","Avocado",
  "Mango","Groundnut","Sunflower","Onion","Cabbage","Kale","Barley",
  "Lettuce","Spinach","Carrot","Garlic","Grape",
];

function CalStatusChip({ status }) {
  const cfg = {
    optimal:   { bg: "#dcfce7", color: "#15803d", label: "Optimal" },
    marginal:  { bg: "#fef9c3", color: "#854d0e", label: "Marginal" },
    unsuitable:{ bg: "#fee2e2", color: "#991b1b", label: "Poor" },
  }[status] || { bg: "#f3f4f6", color: "#6b7280", label: "—" };
  return (
    <div style={{ background: cfg.bg, color: cfg.color, fontSize: 9, fontWeight: 700,
      padding: "1px 5px", borderRadius: 3, display: "inline-block", lineHeight: 1.5 }}>
      {cfg.label}
    </div>
  );
}

function SowDateCalculator({ growthMonths }) {
  const [sowDate, setSowDate] = useState("");
  if (!growthMonths) return null;
  const harvestDate = sowDate
    ? new Date(new Date(sowDate).setMonth(new Date(sowDate).getMonth() + growthMonths))
        .toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })
    : null;
  return (
    <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 10,
      padding: "14px 18px", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#0369a1" }}>Harvest estimator</div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <label style={{ fontSize: 12, color: "#0369a1" }}>Sow on</label>
        <input type="date" value={sowDate} onChange={e => setSowDate(e.target.value)}
          style={{ ...S.input, width: 160, height: 32, paddingTop: 0, paddingBottom: 0, fontSize: 12 }} />
      </div>
      {harvestDate && (
        <div style={{ fontSize: 12, color: "#0369a1" }}>
          Expected harvest: <strong>{harvestDate}</strong>
          <span style={{ color: "#7ab8d4", marginLeft: 8 }}>({growthMonths} months growth)</span>
        </div>
      )}
    </div>
  );
}

function PlantingCalendarTab({ org, lang = "en" }) {
  const [analyses, setAnalyses] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [crop, setCrop] = useState("Maize");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const headers = { Authorization: `Bearer ${getToken()}` };

  useEffect(() => {
    if (!org) return;
    axios.get(`${API}/analyses/org/${org.id}`, { headers })
      .then(r => { setAnalyses(r.data); if (r.data.length) setSelectedId(r.data[0].id); })
      .catch(() => {});
  }, [org]);

  async function fetchCalendar() {
    if (!selectedId) { setErr("Select a saved field first."); return; }
    const analysis = analyses.find(a => a.id === Number(selectedId));
    if (!analysis?.polygon) { setErr("Selected field has no location data."); return; }
    // polygon is stored as a raw [[lon,lat],...] array in the DB
    const p = analysis.polygon;
    const coords = Array.isArray(p) ? p : (p?.coordinates?.[0] || p?.geometry?.coordinates?.[0]);
    if (!coords?.length) { setErr("Cannot determine field location."); return; }
    const lats = coords.map(c => c[1]);
    const lons = coords.map(c => c[0]);
    const lat = lats.reduce((a, b) => a + b, 0) / lats.length;
    const lon = lons.reduce((a, b) => a + b, 0) / lons.length;

    setLoading(true); setErr(null); setData(null);
    try {
      const res = await axios.post(`${API}/planting-calendar`, { lat, lon, crop }, { headers });
      setData(res.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to fetch calendar data.");
    }
    setLoading(false);
  }

  const fieldName = analyses.find(a => a.id === Number(selectedId))?.name || "";

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: "#1a2e1a", marginBottom: 6 }}>Planting Calendar</h2>
      <p style={{ fontSize: 13, color: "#7a9a6a", marginBottom: 20 }}>
        Month-by-month growing conditions based on 20-year NASA climate normals for your field location.
      </p>

      {/* Controls */}
      <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 24 }}>
        <div>
          <label style={{ ...S.label }}>Field</label>
          {analyses.length === 0
            ? <div style={{ fontSize: 12, color: "#999" }}>No saved analyses — save a field first.</div>
            : <select value={selectedId} onChange={e => setSelectedId(e.target.value)}
                style={{ ...S.input, width: 220, height: 38, paddingTop: 0, paddingBottom: 0 }}>
                {analyses.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
          }
        </div>
        <div>
          <label style={{ ...S.label }}>Crop</label>
          <select value={crop} onChange={e => setCrop(e.target.value)}
            style={{ ...S.input, width: 160, height: 38, paddingTop: 0, paddingBottom: 0 }}>
            {CROPS_LIST.map(c => <option key={c} value={c}>{tCrop(c, lang)}</option>)}
          </select>
        </div>
        <button onClick={fetchCalendar} disabled={loading || !analyses.length}
          style={{ height: 38, padding: "0 20px", fontSize: 13, fontWeight: 700,
            background: "#2d5a1b", color: "#fff", border: "none", borderRadius: 8,
            cursor: loading || !analyses.length ? "not-allowed" : "pointer",
            opacity: loading || !analyses.length ? 0.6 : 1 }}>
          {loading ? "Loading…" : "Generate Calendar"}
        </button>
      </div>

      {err && <div style={{ ...S.err, marginBottom: 16 }}>{err}</div>}

      {data && (
        <>
          {/* Summary banner */}
          <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10, padding: "14px 18px", marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#15803d", marginBottom: 4 }}>
              {crop} · {fieldName}
            </div>
            <div style={{ fontSize: 12, color: "#166534" }}>
              Best planting window: <strong>{data.best_planting_start || "—"}</strong>
              {data.growth_months && data.best_planting_start && (() => {
                const sowIdx = MONTHS.findIndex(m => data.best_planting_start?.startsWith(m) || data.calendar?.find(c => c.abbr === m && c.planting));
                const harvestMonth = data.calendar?.find(c => c.planting && c.harvest_month)?.harvest_month;
                return harvestMonth ? <> · Expected harvest: <strong>{harvestMonth}</strong></> : null;
              })()}
              {data.annual_precip_mm != null && <> · Annual rainfall: <strong>{Math.round(data.annual_precip_mm)} mm</strong></>}
            </div>
          </div>

          {/* Sow date → harvest date calculator */}
          <SowDateCalculator growthMonths={data.growth_months} />

          {/* 12-month grid */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10, marginTop: 20 }}>
            {data.calendar.map(m => {
              const isPlanting = m.planting;
              const isGrowing  = m.growing;
              const borderColor = isPlanting ? "#15803d" : isGrowing ? "#86efac" : "#e2ead8";
              const bgColor = isPlanting ? "#f0fdf4" : isGrowing ? "#f8fef8" : "#fff";
              return (
                <div key={m.month} style={{ background: bgColor, border: `1.5px solid ${borderColor}`,
                  borderRadius: 10, padding: "12px 14px", position: "relative" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#1a2e1a" }}>{m.abbr}</div>
                    <CalStatusChip status={m.status} />
                  </div>
                  <div style={{ fontSize: 11, color: "#4a6a3a", lineHeight: 1.8 }}>
                    <div>Temp: {m.temp_c != null ? `${m.temp_c.toFixed(1)}°C` : "—"}</div>
                    <div>Rain: {m.rain_mm != null ? `${Math.round(m.rain_mm)} mm` : "—"}</div>
                    {m.frost_days > 0 && <div style={{ color: "#3b82f6" }}>Frost: {m.frost_days}d</div>}
                  </div>
                  <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {isPlanting && (
                      <span style={{ fontSize: 9, fontWeight: 700, background: "#15803d", color: "#fff",
                        padding: "2px 6px", borderRadius: 4 }}>SOW</span>
                    )}
                    {isPlanting && m.harvest_month && (
                      <span style={{ fontSize: 9, fontWeight: 600, background: "#e0f2fe", color: "#0369a1",
                        padding: "2px 6px", borderRadius: 4 }}>Harvest: {m.harvest_month.slice(0,3)}</span>
                    )}
                    {isGrowing && !isPlanting && (
                      <span style={{ fontSize: 9, fontWeight: 700, background: "#86efac", color: "#14532d",
                        padding: "2px 6px", borderRadius: 4 }}>GROW</span>
                    )}
                    {(m.risks || []).map(r => (
                      <span key={r} style={{ fontSize: 9, fontWeight: 600, background: "#fef9c3", color: "#854d0e",
                        padding: "2px 6px", borderRadius: 4 }}>{r}</span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div style={{ display: "flex", gap: 16, marginTop: 16, fontSize: 11, color: "#7a9a6a", flexWrap: "wrap" }}>
            {[["#15803d","","Sow window"],["#e0f2fe","1px solid #bae6fd","Harvest estimate"],["#86efac","","Growing season"],["#fef9c3","1px solid #fde047","Risk flag"]].map(([bg,border,label]) => (
              <span key={label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ width: 10, height: 10, background: bg, border: border||"none", borderRadius: 2, display: "inline-block" }}/>
                {label}
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Portfolio Map helper ───────────────────────────────────────────────────────
function portfolioHealth(result) {
  // Prefer explicit score fields, fall back to health_status or NDVI
  const score = result?.suitability_score ?? result?.score ?? null;
  if (score != null) return score;
  const h = result?.health_status;
  if (h === "Healthy") return 80;
  if (h === "Fair")    return 50;
  if (h === "Poor")    return 20;
  const ndvi = result?.ndvi;
  if (ndvi != null) return Math.round(Math.min(100, Math.max(0, ndvi * 120)));
  return null;
}

function portfolioScoreColor(score) {
  if (score == null) return "#94a3b8";
  if (score >= 70) return "#15803d";
  if (score >= 40) return "#d97706";
  return "#dc2626";
}

function PortfolioMap({ analyses, focusId }) {
  const mapRef = useRef(null);
  const leafletMap = useRef(null);
  const boundsRef = useRef({});  // id -> L.LatLngBounds

  useEffect(() => {
    if (!mapRef.current) return;

    if (!document.getElementById("leaflet-css-dash")) {
      const link = document.createElement("link");
      link.id = "leaflet-css-dash";
      link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(link);
    }

    if (leafletMap.current) {
      leafletMap.current.remove();
      leafletMap.current = null;
    }

    let cancelled = false;
    import("leaflet").then(({ default: L }) => {
      if (cancelled || !mapRef.current) return;

      if (mapRef.current._leaflet_id) {
        mapRef.current._leaflet_id = null;
      }

      const map = L.map(mapRef.current, { zoomControl: true, preferCanvas: true })
        .setView([20, 0], 2);

      L.tileLayer("https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}", {
        attribution: "© Google", maxZoom: 21,
      }).addTo(map);

      leafletMap.current = map;
      boundsRef.current = {};

      const allLatLngs = [];

      analyses.forEach(a => {
        const p = a.polygon;
        const coords = Array.isArray(p) ? p : (p?.coordinates?.[0] || p?.geometry?.coordinates?.[0]);
        if (!coords?.length) return;

        const latLngs = coords.map(c => [c[1], c[0]]);
        const lats = latLngs.map(c => c[0]);
        const lons = latLngs.map(c => c[1]);
        const centerLat = lats.reduce((x, y) => x + y, 0) / lats.length;
        const centerLon = lons.reduce((x, y) => x + y, 0) / lons.length;

        const score = portfolioHealth(a.result);
        const color = portfolioScoreColor(score);
        const scoreLabel = score != null ? `Score: ${score}` : "No score yet";

        const popup = `<div style="font-family:system-ui,sans-serif;min-width:150px;padding:2px">
          <div style="font-weight:700;font-size:13px;margin-bottom:4px">${a.name}</div>
          <div style="font-size:11px;color:#555">${a.crop}${a.analysis_date ? ` · ${a.analysis_date}` : ""}</div>
          <div style="font-size:11px;margin-top:5px;font-weight:700;color:${color}">${scoreLabel}</div>
        </div>`;

        L.polygon(latLngs, { color, fillColor: color, fillOpacity: 0.3, weight: 2.5 })
          .bindPopup(popup).addTo(map);

        L.circleMarker([centerLat, centerLon], {
          radius: 8, color: "#fff", weight: 2, fillColor: color, fillOpacity: 1,
        }).bindPopup(popup).addTo(map);

        boundsRef.current[a.id] = L.latLngBounds(latLngs);
        allLatLngs.push(...latLngs);
      });

      if (allLatLngs.length) {
        try { map.fitBounds(L.latLngBounds(allLatLngs), { padding: [40, 40], maxZoom: 15 }); }
        catch {}
      }
    });

    return () => {
      cancelled = true;
      if (leafletMap.current) { leafletMap.current.remove(); leafletMap.current = null; }
    };
  }, [analyses.length]); // eslint-disable-line

  // Zoom to focused field when dropdown changes
  useEffect(() => {
    if (!leafletMap.current) return;
    if (!focusId) {
      // Reset to show all fields
      const all = Object.values(boundsRef.current);
      if (all.length) {
        import("leaflet").then(({ default: L }) => {
          const combined = all.reduce((acc, b) => acc.extend(b), L.latLngBounds(all[0]));
          leafletMap.current?.fitBounds(combined, { padding: [40, 40], maxZoom: 15 });
        });
      }
      return;
    }
    const b = boundsRef.current[focusId];
    if (b) leafletMap.current.fitBounds(b, { padding: [60, 60], maxZoom: 17 });
  }, [focusId]);

  return (
    <div ref={mapRef}
      style={{ width: "100%", height: 500, borderRadius: 10, overflow: "hidden",
        border: "1px solid #e2ead8", background: "#1a2e1a" }} />
  );
}

// ── Saved Analyses Tab ─────────────────────────────────────────────────────────
function SavedAnalysesTab({ org, lang = "en" }) {
  const { t } = useTranslation();
  const [analyses, setAnalyses] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [viewMode, setViewMode] = useState("list"); // "list" | "map"
  const [focusId,  setFocusId]  = useState(null);
  const headers = { Authorization: `Bearer ${getToken()}` };

  useEffect(() => {
    if (!org) return;
    axios.get(`${API}/analyses/org/${org.id}`, { headers })
      .then(r => { setAnalyses(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [org]);

  if (selected) return <AnalysisDetail analysis={selected} onBack={() => setSelected(null)} org={org} lang={lang} />;

  function scoreColor(score) {
    if (score == null) return "#94a3b8";
    if (score >= 70) return "#15803d";
    if (score >= 40) return "#d97706";
    return "#dc2626";
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: "#1a2e1a", margin: 0 }}>
          {t("savedAnalysesTab")} — {org?.name}
        </h2>
        {!loading && analyses.length > 0 && (
          <div style={{ display: "flex", border: "1px solid #c8d8c0", borderRadius: 7, overflow: "hidden" }}>
            {["list","map"].map(v => (
              <button key={v} onClick={() => setViewMode(v)}
                style={{ padding: "6px 16px", fontSize: 12, fontWeight: 600, cursor: "pointer",
                  background: viewMode === v ? "#2d5a1b" : "#fff",
                  color: viewMode === v ? "#fff" : "#5a7a4a",
                  border: "none" }}>
                {v === "list" ? t("listView") : t("mapView")}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading ? <><SkCard lines={3} mb={10} /><SkCard lines={3} mb={10} /><SkCard lines={3} mb={0} /></>
        : analyses.length === 0
          ? <div style={{ background: "#fff", borderRadius: 10, padding: 24, border: "1px solid #e2ead8", color: "#999", fontSize: 13 }}>
              {t("noSavedAnalyses")}
            </div>
          : viewMode === "map"
            ? <>
                {/* Field selector dropdown */}
                <div style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 10 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#4a6a3a" }}>Zoom to field:</label>
                  <select value={focusId || ""} onChange={e => setFocusId(e.target.value ? Number(e.target.value) : null)}
                    style={{ ...S.input, width: 240, height: 34, paddingTop: 0, paddingBottom: 0, fontSize: 12 }}>
                    <option value="">— All fields —</option>
                    {analyses.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
                <PortfolioMap analyses={analyses} focusId={focusId} />
                {/* Score legend */}
                <div style={{ display: "flex", gap: 16, marginTop: 12, fontSize: 11, color: "#7a9a6a", flexWrap: "wrap" }}>
                  {[["#15803d","#dcfce7","≥70 — Suitable"],["#d97706","#fef9c3","40–69 — Marginal"],["#dc2626","#fee2e2","<40 — Poor"],["#94a3b8","#f1f5f9","No score"]].map(([c, bg, label]) => (
                    <span key={label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                      <span style={{ width: 10, height: 10, background: bg, border: `2px solid ${c}`, borderRadius: "50%", display: "inline-block" }}/>
                      {label}
                    </span>
                  ))}
                </div>
              </>
            : <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
                {analyses.map(a => {
                  const score = portfolioHealth(a.result);
                  return (
                    <div key={a.id} onClick={() => {
                      axios.get(`${API}/analyses/${a.id}`, { headers }).then(r => setSelected(r.data));
                    }} style={{ background: "#fff", borderRadius: 10, padding: "18px 20px", border: "1px solid #e2ead8", cursor: "pointer", transition: "box-shadow 0.15s" }}
                      onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.08)"}
                      onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: "#1a2e1a" }}>{a.name}</div>
                        {score != null && (
                          <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10,
                            background: score >= 70 ? "#dcfce7" : score >= 40 ? "#fef9c3" : "#fee2e2",
                            color: scoreColor(score) }}>
                            {score}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: "#7a9a6a" }}>{tCrop(a.crop, lang)} · {a.analysis_date || "latest"}</div>
                      <div style={{ fontSize: 11, color: "#bbb", marginTop: 8 }}>
                        Saved {new Date(a.created_at).toLocaleDateString()} · by {a.created_by}
                      </div>
                      {a.last_analyzed_at !== a.created_at && (
                        <div style={{ fontSize: 11, color: "#5a8a3a", marginTop: 2 }}>
                          Last re-analyzed {new Date(a.last_analyzed_at).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
      }
    </div>
  );
}

const HC = s => s === "Healthy" ? "#15803d" : s === "Fair" ? "#854d0e" : "#991b1b";
const HB = s => s === "Healthy" ? "#dcfce7" : s === "Fair" ? "#fef9c3" : "#fee2e2";

function AnalysisDetail({ analysis, onBack, lang = "en" }) {
  const { t } = useTranslation();
  const [current,    setCurrent]    = useState(analysis);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [reMsg,       setReMsg]      = useState(null);
  const [deleting,    setDeleting]   = useState(false);
  const [confirmDel,  setConfirmDel] = useState(false);
  const headers = { Authorization: `Bearer ${getToken()}` };

  const r = current.result || {};
  const indices = r.indices || {};
  const weather = r.weather || {};
  const soil    = r.soil || {};

  // Greyed out if last analysis < 5 days ago (Sentinel-2 revisit)
  const lastAt    = new Date(current.last_analyzed_at || current.created_at);
  const daysSince = (Date.now() - lastAt.getTime()) / (1000 * 60 * 60 * 24);
  const canReanalyze = daysSince >= 5;
  const nextDate  = new Date(lastAt.getTime() + 5 * 24 * 60 * 60 * 1000)
    .toLocaleDateString(undefined, { month: "short", day: "numeric" });

  async function doDelete() {
    setDeleting(true);
    try {
      await axios.delete(`${API}/analyses/${current.id}`, { headers });
      onBack();
    } catch { setReMsg("Delete failed — try again"); setDeleting(false); setConfirmDel(false); }
  }

  async function doReanalyze() {
    setReanalyzing(true); setReMsg(null);
    try {
      const res = await axios.post(`${API}/analyses/reanalyze`,
        { analysis_id: current.id }, { headers });
      setCurrent(c => ({ ...c, result: res.data.result, last_analyzed_at: res.data.last_analyzed_at }));
      setReMsg("Re-analyzed successfully");
    } catch { setReMsg("Re-analysis failed — try again"); }
    setReanalyzing(false);
  }

  return (
    <div style={{ maxWidth: 900 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        <button onClick={onBack} style={{ ...S.btnGhost, fontSize: 12 }}>← {t("overviewTab")}</button>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: "#1a2e1a", margin: 0 }}>{current.name}</h2>
        <div style={{ fontSize: 12, color: "#7a9a6a" }}>{tCrop(current.crop, lang)} · {current.analysis_date || current.created_at?.slice(0,10)}</div>
        {r.health_status && (
          <span style={{ fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 12, background: HB(r.health_status), color: HC(r.health_status) }}>
            {r.health_status}
          </span>
        )}
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
          {reMsg && <span style={{ fontSize: 12, color: reMsg.includes("fail") ? "#991b1b" : "#15803d" }}>{reMsg}</span>}
          <button onClick={() => printFieldReport({ ...r, crop_type: current.crop })}
            style={{ padding:"7px 14px", fontSize:12, fontWeight:600, borderRadius:7, border:"1px solid #c8d8c0", background:"#fff", color:"#2d5a1b", cursor:"pointer" }}>
            Print PDF
          </button>
          {confirmDel ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 12, color: "#991b1b", fontWeight: 600 }}>{t("deleteAnalysis")}</span>
              <button onClick={doDelete} disabled={deleting}
                style={{ padding: "6px 14px", fontSize: 12, fontWeight: 700, borderRadius: 7, border: "none", cursor: "pointer", background: "#991b1b", color: "#fff" }}>
                {deleting ? t("deletingBtn") : t("yesDeleteBtn")}
              </button>
              <button onClick={() => setConfirmDel(false)}
                style={{ padding: "6px 12px", fontSize: 12, borderRadius: 7, border: "1px solid #ccc", background: "#fff", cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          ) : (
            <button onClick={() => setConfirmDel(true)}
              style={{ padding: "7px 14px", fontSize: 12, fontWeight: 600, borderRadius: 7, border: "1px solid #fca5a5", background: "#fff5f5", color: "#991b1b", cursor: "pointer" }}>
              {t("deleteBtn")}
            </button>
          )}
          <div title={canReanalyze ? "Run a fresh analysis with today's satellite data" : `New Sentinel-2 data available ~${nextDate}`}>
            <button onClick={canReanalyze ? doReanalyze : undefined} disabled={!canReanalyze || reanalyzing}
              style={{ padding: "7px 16px", fontSize: 12, fontWeight: 700, borderRadius: 7, border: "none", cursor: canReanalyze ? "pointer" : "not-allowed",
                background: canReanalyze ? "#2d5a1b" : "#e2ead8", color: canReanalyze ? "#fff" : "#a0b898" }}>
              {reanalyzing ? "Analyzing…" : canReanalyze ? "Re-analyze" : `Re-analyze · new data ~${nextDate}`}
            </button>
          </div>
        </div>
      </div>

      {/* Key metrics */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 12, marginBottom: 16 }}>
        {r.ndvi        != null && <StatCard label="NDVI"       value={r.ndvi} color={r.ndvi > 0.5 ? "#15803d" : r.ndvi > 0.3 ? "#854d0e" : "#991b1b"} />}
        {r.ndvi_change != null && <StatCard label="3-mo Trend" value={(r.ndvi_change >= 0 ? "+" : "") + r.ndvi_change} color={r.ndvi_change >= 0 ? "#15803d" : "#991b1b"} />}
        {r.moisture    != null && <StatCard label="Moisture"   value={r.moisture} />}
        {r.nitrogen    != null && <StatCard label="Nitrogen"   value={r.nitrogen} />}
        {r.heterogeneity != null && <StatCard label="Variability" value={r.heterogeneity} />}
      </div>

      {/* Vegetation indices grid */}
      {Object.keys(indices).length > 0 && (
        <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e2ead8", padding: "16px 20px", marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#7a9a6a", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 12 }}>Vegetation Indices</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 10 }}>
            {Object.entries(indices).map(([key, idx]) => (
              <div key={key} style={{ background: "#f8fbf5", borderRadius: 8, padding: "10px 12px" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#7a9a6a", textTransform: "uppercase", letterSpacing: 1 }}>{key}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#1a2e1a", margin: "2px 0" }}>{idx.mean ?? "—"}</div>
                <div style={{ fontSize: 10, color: "#aaa" }}>{idx.label}</div>
                {idx.p10 != null && <div style={{ fontSize: 9, color: "#bbb", marginTop: 2 }}>{idx.p10} → {idx.p90}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Weather + Soil side by side */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        {!weather.error && weather.avg_temp_7d != null && (
          <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e2ead8", padding: "16px 20px" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#7a9a6a", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>7-Day Weather</div>
            {[["Avg Temp", `${weather.avg_temp_7d}°C`], ["Max Temp", `${weather.max_temp_7d}°C`],
              ["Rainfall", `${weather.total_precip_7d_mm}mm`], ["Humidity", `${weather.avg_rh_7d_pct}%`],
              ["ET₀", `${weather.et0_7d_mm}mm`]].map(([l,v]) => (
              <div key={l} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "3px 0", borderBottom: "1px solid #f5f5f0" }}>
                <span style={{ color: "#7a9a6a" }}>{l}</span><span style={{ fontWeight: 600, color: "#1a2e1a" }}>{v}</span>
              </div>
            ))}
          </div>
        )}
        {!soil.error && soil.ph != null && (
          <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e2ead8", padding: "16px 20px" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#7a9a6a", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>Soil Profile</div>
            {[["pH", soil.ph], ["Clay", `${soil.clay_pct}%`], ["Sand", `${soil.sand_pct}%`],
              ["Org. Carbon", `${soil.soc_g_kg} g/kg`], ["CEC", `${soil.cec_mmol_kg} mmol/kg`]].map(([l,v]) => (
              <div key={l} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "3px 0", borderBottom: "1px solid #f5f5f0" }}>
                <span style={{ color: "#7a9a6a" }}>{l}</span><span style={{ fontWeight: 600, color: "#1a2e1a" }}>{v}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Risks */}
      {r.risks?.length > 0 && (
        <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e2ead8", padding: "16px 20px" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#7a9a6a", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 12 }}>Risks Detected</div>
          {r.risks.map((risk, i) => (
            <div key={i} style={{ padding: "10px 0", borderBottom: i < r.risks.length-1 ? "1px solid #f5f5f0" : "none" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 10,
                  background: risk.level === "Severe" ? "#fee2e2" : risk.level === "High" ? "#ffedd5" : "#fef9c3",
                  color: risk.level === "Severe" ? "#991b1b" : risk.level === "High" ? "#9a3412" : "#854d0e" }}>
                  {risk.level}
                </span>
                <span style={{ fontSize: 13, fontWeight: 600, color: "#1a2e1a" }}>{risk.name}</span>
              </div>
              <div style={{ fontSize: 12, color: "#7a9a6a" }}>{risk.recommendation}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function UsersTab() {
  const [authorized, setAuthorized] = useState([]);
  const [pending,    setPending]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [newEmail,   setNewEmail]   = useState("");
  const [msg,        setMsg]        = useState(null);

  const headers = { Authorization: `Bearer ${getToken()}` };

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/auth/admin/users`,    { headers }),
      axios.get(`${API}/auth/admin/pending`,  { headers }),
    ]).then(([u, p]) => {
      setAuthorized(u.data);
      setPending(p.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function approveUser(email, approve_token) {
    await axios.post(`${API}/auth/admin/approve`, { email, approve_token }, { headers });
    setMsg(`✓ ${email} approved — magic link sent.`);
    load();
  }

  async function revokeUser(email) {
    if (!window.confirm(`Revoke access for ${email}?`)) return;
    await axios.post(`${API}/auth/admin/revoke`, { email }, { headers });
    setMsg(`✓ Access revoked for ${email}.`);
    load();
  }

  async function addUser(e) {
    e.preventDefault();
    if (!newEmail.trim()) return;
    await axios.post(`${API}/auth/admin/add-user`, { email: newEmail.trim().toLowerCase() }, { headers });
    setMsg(`✓ ${newEmail} added.`);
    setNewEmail("");
    load();
  }

  const rowStyle = { display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #f0f6ea" };

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: "#1a2e1a", marginBottom: 20 }}>Users &amp; Access</h2>
      {msg && <div style={{ ...S.ok, marginBottom: 16 }}>{msg}</div>}

      {/* Pending requests */}
      {pending.length > 0 && (
        <div style={{ background: "#fffbeb", borderRadius: 10, padding: "20px 24px", border: "1px solid #fde68a", marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#92400e", marginBottom: 12 }}>⚠ Pending access requests ({pending.length})</div>
          {pending.map(r => (
            <div key={r.email} style={rowStyle}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{r.email}</div>
                <div style={{ fontSize: 11, color: "#999" }}>
                  Requested {new Date(r.requested_at).toLocaleDateString()}
                  {r.org_name && <> · <span style={{ fontWeight: 600, color: "#5a8a3a" }}>{r.org_name}</span></>}
                </div>
              </div>
              <button onClick={() => approveUser(r.email, r.approve_token)}
                style={{ padding: "6px 14px", fontSize: 12, fontWeight: 600, background: "#2d5a1b", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" }}>
                Approve {r.org_name ? `for ${r.org_name}` : ""}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add user manually */}
      <div style={{ background: "#fff", borderRadius: 10, padding: "20px 24px", border: "1px solid #e2ead8", marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#1a2e1a", marginBottom: 12 }}>Add user directly (use Organizations tab to assign org)</div>
        <form onSubmit={addUser} style={{ display: "flex", gap: 8 }}>
          <input type="email" placeholder="email@example.com" value={newEmail} onChange={e => setNewEmail(e.target.value)}
            style={{ ...S.input, flex: 1, marginTop: 0 }} />
          <button type="submit" style={{ ...S.btn, width: "auto", padding: "10px 18px", marginTop: 0 }}>Add</button>
        </form>
      </div>

      {/* Authorized users */}
      <div style={{ background: "#fff", borderRadius: 10, padding: "20px 24px", border: "1px solid #e2ead8" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#1a2e1a", marginBottom: 12 }}>
          Authorized users ({authorized.length})
        </div>
        {loading ? <><SkRow cols={3} mb={10} /><SkRow cols={3} mb={10} /><SkRow cols={3} mb={0} /></> : authorized.map(u => (
          <div key={u.email} style={rowStyle}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{u.email}</div>
              <div style={{ fontSize: 11, color: "#999" }}>Added {new Date(u.approved_at).toLocaleDateString()}</div>
            </div>
            {u.email !== "nico@openfarmanalytics.org" && (
              <button onClick={() => revokeUser(u.email)}
                style={{ padding: "5px 12px", fontSize: 11, fontWeight: 600, background: "#fff", color: "#c0392b", border: "1px solid #fecaca", borderRadius: 6, cursor: "pointer" }}>
                Revoke
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ActivityTab({ org, lang = "en" }) {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${getToken()}` };

  useEffect(() => {
    if (!org) return;
    axios.get(`${API}/analyses/org/${org.id}`, { headers })
      .then(r => {
        // Build a flat event list from created_at + last_analyzed_at per analysis
        const evts = [];
        r.data.forEach(a => {
          evts.push({ type: "saved", date: a.created_at, name: a.name, crop: a.crop, by: a.created_by });
          if (a.last_analyzed_at && a.last_analyzed_at !== a.created_at) {
            evts.push({ type: "reanalyzed", date: a.last_analyzed_at, name: a.name, crop: a.crop, by: a.created_by });
          }
        });
        evts.sort((a, b) => new Date(b.date) - new Date(a.date));
        setEvents(evts);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [org]);

  const typeConfig = {
    saved:      { label: "Analysis saved",     dot: "#15803d", bg: "#dcfce7" },
    reanalyzed: { label: "Re-analyzed",        dot: "#2563eb", bg: "#dbeafe" },
  };

  // Group by date
  const grouped = [];
  let lastDate = null;
  events.forEach(e => {
    const d = new Date(e.date).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
    if (d !== lastDate) { grouped.push({ divider: d }); lastDate = d; }
    grouped.push(e);
  });

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: "#1a2e1a", marginBottom: 20 }}>{t("fieldActivityTab")} — {org?.name}</h2>
      {loading ? <><SkRow cols={4} mb={10} /><SkRow cols={4} mb={10} /><SkRow cols={4} mb={0} /></>
        : events.length === 0
          ? <div style={{ background: "#fff", borderRadius: 10, padding: 24, border: "1px solid #e2ead8", color: "#999", fontSize: 13 }}>
              {t("noActivityYet")}
            </div>
          : <div style={{ position: "relative", paddingLeft: 24 }}>
              {/* Vertical line */}
              <div style={{ position: "absolute", left: 7, top: 0, bottom: 0, width: 2, background: "#e2ead8" }} />
              {grouped.map((item, i) =>
                item.divider
                  ? <div key={`d${i}`} style={{ fontSize: 11, fontWeight: 700, color: "#7a9a6a", textTransform: "uppercase",
                      letterSpacing: "0.06em", margin: "20px 0 10px 0", paddingLeft: 16 }}>
                      {item.divider}
                    </div>
                  : <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 10, position: "relative" }}>
                      {/* Dot */}
                      <div style={{ width: 14, height: 14, borderRadius: "50%", background: typeConfig[item.type]?.dot || "#94a3b8",
                        border: "2px solid #fff", flexShrink: 0, marginTop: 2, position: "relative", zIndex: 1 }} />
                      <div style={{ background: "#fff", border: "1px solid #e2ead8", borderRadius: 8,
                        padding: "10px 14px", flex: 1 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                          <div>
                            <span style={{ fontSize: 13, fontWeight: 700, color: "#1a2e1a" }}>{item.name}</span>
                            <span style={{ fontSize: 11, color: "#7a9a6a", marginLeft: 8 }}>{tCrop(item.crop, lang)}</span>
                          </div>
                          <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 10,
                            background: typeConfig[item.type]?.bg, color: typeConfig[item.type]?.dot, flexShrink: 0 }}>
                            {typeConfig[item.type]?.label}
                          </span>
                        </div>
                        <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>
                          {new Date(item.date).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })} · {item.by}
                        </div>
                      </div>
                    </div>
              )}
            </div>
      }
    </div>
  );
}

const SCOUT_CATEGORIES = ["Pest", "Disease", "Irrigation", "Soil", "Weed", "Other"];
const SCOUT_SEVERITY   = ["Low", "Medium", "High"];
const SEV_COLOR = { Low: "#15803d", Medium: "#d97706", High: "#dc2626" };
const SEV_BG    = { Low: "#dcfce7", Medium: "#fef9c3", High: "#fee2e2" };

function ScoutTab({ org }) {
  const { t } = useTranslation();
  const [reports, setReports]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg]           = useState(null);
  const [photo, setPhoto]       = useState(null);   // base64 string
  const [photoPreview, setPhotoPreview] = useState(null);
  const [form, setForm] = useState({
    field_name: "", category: "Pest", severity: "Medium", notes: "", observed_at: new Date().toISOString().slice(0,10),
  });
  const headers = { Authorization: `Bearer ${getToken()}` };

  function loadReports() {
    if (!org) return;
    axios.get(`${API}/scout-reports/org/${org.id}`, { headers })
      .then(r => { setReports(r.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  }

  useEffect(() => { loadReports(); }, [org]);

  function handlePhoto(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    // Resize to max 800px and compress before base64 encoding
    const reader = new FileReader();
    reader.onload = ev => {
      const img = new Image();
      img.onload = () => {
        const MAX = 800;
        const scale = Math.min(1, MAX / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width  = Math.round(img.width  * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        const b64 = canvas.toDataURL("image/jpeg", 0.75);
        setPhoto(b64);
        setPhotoPreview(b64);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.field_name.trim() || !form.notes.trim()) { setMsg({ err: true, text: "Field name and notes are required." }); return; }
    setSubmitting(true); setMsg(null);
    try {
      await axios.post(`${API}/scout-reports`, { ...form, org_id: org.id, photo: photo || null }, { headers });
      setMsg({ err: false, text: "Report submitted." });
      setShowForm(false);
      setForm({ field_name: "", category: "Pest", severity: "Medium", notes: "", observed_at: new Date().toISOString().slice(0,10) });
      setPhoto(null); setPhotoPreview(null);
      loadReports();
    } catch { setMsg({ err: true, text: "Submission failed — try again." }); }
    setSubmitting(false);
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: "#1a2e1a", margin: 0 }}>{t("scoutReportsTab")} — {org?.name}</h2>
        <button onClick={() => { setShowForm(s => !s); setMsg(null); }}
          style={{ padding: "8px 18px", fontSize: 13, fontWeight: 700, background: "#2d5a1b", color: "#fff",
            border: "none", borderRadius: 8, cursor: "pointer" }}>
          {showForm ? t("cancel") : t("newReportBtn")}
        </button>
      </div>

      {msg && <div style={{ ...(msg.err ? S.err : S.ok), marginBottom: 16 }}>{msg.text}</div>}

      {showForm && (
        <form onSubmit={submit} style={{ background: "#fff", border: "1px solid #e2ead8", borderRadius: 10,
          padding: "20px 24px", marginBottom: 24 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 14, marginBottom: 14 }}>
            <div>
              <label style={S.label}>{t("fieldNameLabel")}</label>
              <input value={form.field_name} onChange={e => setForm(f => ({ ...f, field_name: e.target.value }))}
                placeholder="e.g. North Block" style={S.input} />
            </div>
            <div>
              <label style={S.label}>{t("observedOnLabel")}</label>
              <input type="date" value={form.observed_at} onChange={e => setForm(f => ({ ...f, observed_at: e.target.value }))}
                style={S.input} />
            </div>
            <div>
              <label style={S.label}>{t("categoryLabel")}</label>
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                style={{ ...S.input, height: 40, paddingTop: 0, paddingBottom: 0 }}>
                {SCOUT_CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={S.label}>{t("severityLabel")}</label>
              <select value={form.severity} onChange={e => setForm(f => ({ ...f, severity: e.target.value }))}
                style={{ ...S.input, height: 40, paddingTop: 0, paddingBottom: 0 }}>
                {SCOUT_SEVERITY.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={S.label}>{t("notesLabel")}</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Describe what you observed — affected area, estimated coverage, any action already taken…"
              rows={4} style={{ ...S.input, resize: "vertical", lineHeight: 1.5 }} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={S.label}>{t("photoOptional")}</label>
            <input type="file" accept="image/*" onChange={handlePhoto}
              style={{ fontSize: 12, color: "#444" }} />
            {photoPreview && (
              <div style={{ marginTop: 8, display: "flex", alignItems: "flex-start", gap: 10 }}>
                <img src={photoPreview} alt="preview" style={{ width: 120, height: 90, objectFit: "cover", borderRadius: 6, border: "1px solid #e2ead8" }} />
                <button type="button" onClick={() => { setPhoto(null); setPhotoPreview(null); }}
                  style={{ fontSize: 11, color: "#991b1b", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                  {t("removeBtn")}
                </button>
              </div>
            )}
          </div>
          <button type="submit" disabled={submitting}
            style={{ padding: "9px 22px", fontSize: 13, fontWeight: 700, background: "#2d5a1b", color: "#fff",
              border: "none", borderRadius: 8, cursor: submitting ? "not-allowed" : "pointer", opacity: submitting ? 0.7 : 1 }}>
            {submitting ? t("submittingBtn") : t("submitReportBtn")}
          </button>
        </form>
      )}

      {loading ? <><SkCard lines={4} mb={10} /><SkCard lines={4} mb={0} /></>
        : reports.length === 0
          ? <div style={{ background: "#fff", borderRadius: 10, padding: 24, border: "1px solid #e2ead8", color: "#999", fontSize: 13 }}>
              {t("noScoutReports")}
            </div>
          : <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {reports.map(r => (
                <div key={r.id} style={{ background: "#fff", border: "1px solid #e2ead8", borderRadius: 10, padding: "14px 18px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                    <div>
                      <span style={{ fontSize: 14, fontWeight: 700, color: "#1a2e1a" }}>{r.field_name}</span>
                      <span style={{ fontSize: 12, color: "#7a9a6a", marginLeft: 10 }}>{r.category}</span>
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 9px", borderRadius: 10,
                        background: SEV_BG[r.severity] || "#f3f4f6", color: SEV_COLOR[r.severity] || "#555" }}>
                        {r.severity}
                      </span>
                      <span style={{ fontSize: 11, color: "#aaa" }}>
                        {new Date(r.observed_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 12, marginTop: 8, alignItems: "flex-start" }}>
                    <p style={{ fontSize: 13, color: "#444", margin: 0, lineHeight: 1.5, flex: 1 }}>{r.notes}</p>
                    {r.photo && (
                      <img src={r.photo} alt="scout" style={{ width: 100, height: 75, objectFit: "cover",
                        borderRadius: 6, border: "1px solid #e2ead8", flexShrink: 0, cursor: "pointer" }}
                        onClick={() => window.open(r.photo, "_blank")} />
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: "#bbb", marginTop: 6 }}>{t("loggedBy")} {r.created_by}</div>
                </div>
              ))}
            </div>
      }
    </div>
  );
}

// ── Irrigation Tab ────────────────────────────────────────────────────────────
const VERDICT_CFG = {
  rainfed:      { bg: "#dcfce7", color: "#15803d", label: "Rainfed Viable" },
  supplemental: { bg: "#fef9c3", color: "#854d0e", label: "Supplemental Irrigation" },
  moderate:     { bg: "#ffedd5", color: "#9a3412", label: "Moderate Irrigation Required" },
  intensive:    { bg: "#fee2e2", color: "#991b1b", label: "Intensive Irrigation Required" },
  unknown:      { bg: "#f3f4f6", color: "#6b7280", label: "Unknown" },
};

function IrrigationTab({ org }) {
  const [analyses,     setAnalyses]     = useState([]);
  const [selectedId,   setSelectedId]   = useState("");
  const [compareCrop,  setCompareCrop]  = useState("");
  const [data,         setData]         = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState(null);
  const [scheduleView, setScheduleView] = useState("monthly"); // monthly | daily
  const [forecast,     setForecast]     = useState(null);
  const headers = { Authorization: `Bearer ${getToken()}` };

  // Load saved analyses
  useEffect(() => {
    if (!org) return;
    axios.get(`${API}/analyses/org/${org.id}`, { headers }).then(r => {
      const list = r.data?.analyses || [];
      setAnalyses(list);
      if (list.length > 0) setSelectedId(String(list[0].id));
    }).catch(() => {});
  }, [org?.id]);

  const selected = analyses.find(a => String(a.id) === selectedId);
  const crop      = selected?.result?.crop || selected?.result?.inputs?.crop || "";
  const fieldHa   = selected?.result?.area_ha || selected?.result?.inputs?.area_ha || 1.0;
  const polygon   = selected?.polygon;
  const coords    = Array.isArray(polygon) ? polygon
                  : polygon?.coordinates?.[0] || polygon?.geometry?.coordinates?.[0];
  const centroid  = coords
    ? { lat: coords.reduce((s, p) => s + p[1], 0) / coords.length,
        lon: coords.reduce((s, p) => s + p[0], 0) / coords.length }
    : null;

  // Fetch irrigation data when selection/compare changes
  useEffect(() => {
    if (!centroid || !crop) { setData(null); return; }
    setLoading(true); setError(null);
    axios.post(`${API}/irrigation-advanced`, {
      lat: centroid.lat, lon: centroid.lon, crop, field_ha: fieldHa,
      compare_crop: compareCrop || null,
    }, { headers }).then(r => {
      if (r.data.error) { setError(r.data.error); setData(null); }
      else setData(r.data);
      setLoading(false);
    }).catch(e => { setError(e.message); setLoading(false); });
  }, [selectedId, compareCrop, centroid?.lat, centroid?.lon]);

  // Fetch Open-Meteo 7-day forecast for daily schedule
  useEffect(() => {
    if (!centroid) return;
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${centroid.lat.toFixed(4)}&longitude=${centroid.lon.toFixed(4)}&daily=temperature_2m_max,temperature_2m_min,precipitation_sum,weathercode,windspeed_10m_max,et0_fao_evapotranspiration&forecast_days=7&timezone=auto`;
    fetch(url).then(r => r.json()).then(d => setForecast(d)).catch(() => {});
  }, [centroid?.lat, centroid?.lon]);

  const style = { fontFamily: "system-ui, sans-serif" };

  const NetCell = ({ mm }) => {
    const bg = mm === 0 ? "#f0fdf4" : mm < 30 ? "#fef9c3" : mm < 60 ? "#ffedd5" : "#fee2e2";
    const co = mm === 0 ? "#15803d" : mm < 30 ? "#854d0e" : mm < 60 ? "#9a3412" : "#991b1b";
    return (
      <td style={{ textAlign: "right", padding: "5px 10px", background: bg, color: co, fontWeight: 600, fontSize: 12 }}>
        {mm === 0 ? "None" : `${mm} mm`}
      </td>
    );
  };

  const SuitCard = ({ suit, cropName }) => {
    if (!suit) return null;
    const cfg = VERDICT_CFG[suit.verdict] || VERDICT_CFG.unknown;
    return (
      <div style={{ background: cfg.bg, border: `1px solid ${cfg.color}30`, borderRadius: 10, padding: "14px 18px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <span style={{ fontWeight: 700, fontSize: 13, color: cfg.color }}>{cropName}</span>
          <span style={{ fontSize: 11, fontWeight: 700, background: cfg.color + "20", color: cfg.color,
            padding: "2px 8px", borderRadius: 10 }}>{cfg.label}</span>
        </div>
        <p style={{ fontSize: 12, color: "#374151", margin: "0 0 8px 0", lineHeight: 1.6 }}>{suit.description}</p>
        <div style={{ display: "flex", gap: 20, fontSize: 11, color: "#6b7280", flexWrap: "wrap" }}>
          <span>Annual rainfall: <strong>{suit.annual_precip_mm} mm</strong></span>
          <span>Seasonal ETc: <strong>{suit.etc_season_mm} mm</strong></span>
          <span>Deficit: <strong>{suit.annual_deficit_mm} mm ({suit.deficit_pct}%)</strong></span>
          <span>Crop water need: <strong>{suit.r_min}–{suit.r_max} mm/yr</strong></span>
        </div>
      </div>
    );
  };

  // Daily schedule from Open-Meteo forecast
  const DailySchedule = () => {
    if (!forecast?.daily) return <div style={{ display:"flex", gap:8 }}>{Array.from({length:7},(_,i)=><div key={i} style={{flex:1}}><Sk w="100%" h={60} r={8} mb={6}/><Sk w="60%" h={10} r={3}/></div>)}</div>;
    const d = forecast.daily;
    const today = new Date();
    return (
      <div>
        <div style={{ fontSize: 12, color: "#7a9a6a", marginBottom: 10 }}>
          Best time to irrigate: <strong>early morning (5–8 AM)</strong> — lower evaporation, lower wind, better absorption. Avoid midday irrigation.
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#f0f6ea" }}>
                {["Date","High","Low","Rain","ET0","Net Need","Recommendation"].map(h => (
                  <th key={h} style={{ padding: "7px 10px", textAlign: "left", fontWeight: 700, fontSize: 11,
                    color: "#374151", borderBottom: "1px solid #e2ead8", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {d.time.map((dt, i) => {
                const rain    = d.precipitation_sum[i] ?? 0;
                const et0     = d.et0_fao_evapotranspiration?.[i] ?? 0;
                const kc_mid  = data?.kc_mid ?? 1.0;
                const etc     = round1(et0 * kc_mid);
                const net     = Math.max(0, round1(etc - rain));
                const wcode   = d.weathercode?.[i];
                const isRainy = rain > 5;
                const dateStr = new Date(dt).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
                let rec, recColor;
                if (rain > 10) {
                  rec = "Skip — sufficient rainfall"; recColor = "#15803d";
                } else if (net < 2) {
                  rec = "No irrigation needed"; recColor = "#15803d";
                } else if (net < 5) {
                  rec = `Light irrigation ~${net} mm`; recColor = "#854d0e";
                } else {
                  rec = `Irrigate ~${net} mm (${Math.round(net/1000 * fieldHa * 10000)} m³)`; recColor = "#991b1b";
                }
                return (
                  <tr key={dt} style={{ borderBottom: "1px solid #f0f0f0" }}>
                    <td style={{ padding: "6px 10px", fontWeight: 600, color: "#1a2e1a" }}>{dateStr}</td>
                    <td style={{ padding: "6px 10px", color: "#374151" }}>{d.temperature_2m_max[i]?.toFixed(0)}°C</td>
                    <td style={{ padding: "6px 10px", color: "#374151" }}>{d.temperature_2m_min[i]?.toFixed(0)}°C</td>
                    <td style={{ padding: "6px 10px", color: isRainy ? "#2563eb" : "#374151" }}>{rain.toFixed(1)} mm</td>
                    <td style={{ padding: "6px 10px", color: "#374151" }}>{et0.toFixed(1)} mm</td>
                    <NetCell mm={net} />
                    <td style={{ padding: "6px 10px", color: recColor, fontWeight: 600, fontSize: 11 }}>{rec}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: 11, color: "#aaa", marginTop: 8 }}>
          ET0 from Open-Meteo FAO-56 Penman-Monteith. Net need = ETc (ET0 x Kc) minus rainfall.
        </div>
      </div>
    );
  };

  function round1(v) { return Math.round(v * 10) / 10; }

  if (!org) return (
    <div style={{ color: "#999", fontSize: 13 }}>No organization selected.</div>
  );

  return (
    <div style={style}>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: "#1a2e1a", marginBottom: 4 }}>Irrigation Analysis</h2>

      {/* Field selector */}
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 20 }}>
        <div>
          <label style={{ fontSize: 12, color: "#7a9a6a", fontWeight: 600, marginRight: 6 }}>Field</label>
          <select value={selectedId} onChange={e => setSelectedId(e.target.value)}
            style={{ padding: "6px 10px", fontSize: 13, borderRadius: 7, border: "1px solid #dde8d0", background: "#fff" }}>
            {analyses.map(a => (
              <option key={a.id} value={a.id}>
                {a.name || a.id} — {tCrop(a.result?.crop || a.result?.inputs?.crop || "Unknown crop", lang)}
              </option>
            ))}
          </select>
        </div>
        {selected && (
          <div style={{ fontSize: 12, color: "#7a9a6a" }}>
            Crop: <strong style={{ color: "#1a2e1a" }}>{crop}</strong>
            &nbsp;|&nbsp;Area: <strong style={{ color: "#1a2e1a" }}>{fieldHa?.toFixed?.(1) || "?"} ha</strong>
          </div>
        )}
      </div>

      {!selected && (
        <div style={{ color: "#999", fontSize: 13 }}>No saved analyses found. Run a field analysis first.</div>
      )}

      {selected && !centroid && (
        <div style={{ color: "#e66", fontSize: 13 }}>Cannot determine field location from this analysis.</div>
      )}

      {selected && centroid && loading && (
        <div style={{ marginTop: 12 }}><Sk w="100%" h={14} r={5} mb={8} /><Sk w="75%" h={14} r={5} mb={8} /><Sk w="90%" h={14} r={5} /></div>
      )}
      {error && <div style={{ color: "#c33", fontSize: 13 }}>Error: {error}</div>}

      {data && !loading && (<>

        {/* ── A. Crop Water Demand Comparison ── */}
        <section style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2ead8", padding: 20, marginBottom: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 14 }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: "#1a2e1a", margin: 0 }}>Crop Water Demand Comparison</h3>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <label style={{ fontSize: 12, color: "#7a9a6a", fontWeight: 600 }}>Compare against</label>
              <select value={compareCrop} onChange={e => setCompareCrop(e.target.value)}
                style={{ padding: "5px 10px", fontSize: 12, borderRadius: 7, border: "1px solid #dde8d0", background: "#fff" }}>
                <option value="">— none —</option>
                {CROPS_LIST.filter(c => c !== crop).map(c => <option key={c} value={c}>{tCrop(c, lang)}</option>)}
              </select>
            </div>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: "#f0f6ea" }}>
                  <th style={{ padding: "7px 10px", textAlign: "left", fontWeight: 700, fontSize: 11, color: "#374151", borderBottom: "1px solid #e2ead8" }}>Month</th>
                  <th style={{ padding: "7px 10px", textAlign: "right", fontWeight: 700, fontSize: 11, color: "#374151", borderBottom: "1px solid #e2ead8" }}>ET0 (mm)</th>
                  <th style={{ padding: "7px 10px", textAlign: "right", fontWeight: 700, fontSize: 11, color: "#374151", borderBottom: "1px solid #e2ead8" }}>Rain (mm)</th>
                  <th style={{ padding: "7px 10px", textAlign: "right", fontWeight: 700, fontSize: 11, color: "#2d5a1b", borderBottom: "1px solid #e2ead8" }}>{crop} ETc</th>
                  <th style={{ padding: "7px 10px", textAlign: "right", fontWeight: 700, fontSize: 11, color: "#2d5a1b", borderBottom: "1px solid #e2ead8" }}>{crop} Net Need</th>
                  {data.compare_monthly && <>
                    <th style={{ padding: "7px 10px", textAlign: "right", fontWeight: 700, fontSize: 11, color: "#7c3aed", borderBottom: "1px solid #e2ead8" }}>{compareCrop} ETc</th>
                    <th style={{ padding: "7px 10px", textAlign: "right", fontWeight: 700, fontSize: 11, color: "#7c3aed", borderBottom: "1px solid #e2ead8" }}>{compareCrop} Net Need</th>
                  </>}
                </tr>
              </thead>
              <tbody>
                {data.monthly.map((mo, i) => {
                  const cmp = data.compare_monthly?.[i];
                  return (
                    <tr key={mo.month} style={{ borderBottom: "1px solid #f0f0f0" }}>
                      <td style={{ padding: "5px 10px", fontWeight: 600, color: "#1a2e1a" }}>{mo.month}</td>
                      <td style={{ padding: "5px 10px", textAlign: "right", color: "#374151" }}>{mo.eto_mm}</td>
                      <td style={{ padding: "5px 10px", textAlign: "right", color: "#2563eb" }}>{mo.precip_mm}</td>
                      <td style={{ padding: "5px 10px", textAlign: "right", color: "#2d5a1b" }}>{mo.etc_mm}</td>
                      <NetCell mm={mo.net_mm} />
                      {cmp && <>
                        <td style={{ padding: "5px 10px", textAlign: "right", color: "#7c3aed" }}>{cmp.etc_mm}</td>
                        <NetCell mm={cmp.net_mm} />
                      </>}
                    </tr>
                  );
                })}
                {/* Totals */}
                <tr style={{ background: "#f0f6ea", fontWeight: 700 }}>
                  <td style={{ padding: "7px 10px", fontSize: 12, color: "#1a2e1a" }}>Annual total</td>
                  <td style={{ padding: "7px 10px", textAlign: "right", fontSize: 12 }}>{data.monthly.reduce((s,m) => s+m.eto_mm, 0).toFixed(0)}</td>
                  <td style={{ padding: "7px 10px", textAlign: "right", fontSize: 12, color: "#2563eb" }}>{data.monthly.reduce((s,m) => s+m.precip_mm, 0).toFixed(0)}</td>
                  <td style={{ padding: "7px 10px", textAlign: "right", fontSize: 12, color: "#2d5a1b" }}>{data.monthly.reduce((s,m) => s+m.etc_mm, 0).toFixed(0)}</td>
                  <td style={{ padding: "7px 10px", textAlign: "right", fontSize: 12, color: "#991b1b" }}>{data.monthly.reduce((s,m) => s+m.net_mm, 0).toFixed(0)} mm</td>
                  {data.compare_monthly && <>
                    <td style={{ padding: "7px 10px", textAlign: "right", fontSize: 12, color: "#7c3aed" }}>{data.compare_monthly.reduce((s,m) => s+m.etc_mm, 0).toFixed(0)}</td>
                    <td style={{ padding: "7px 10px", textAlign: "right", fontSize: 12, color: "#991b1b" }}>{data.compare_monthly.reduce((s,m) => s+m.net_mm, 0).toFixed(0)} mm</td>
                  </>}
                </tr>
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 11, color: "#aaa", marginTop: 8 }}>
            ETc uses peak Kc (Kc_mid={data.kc_mid}) — representative of active growing season. Net need = ETc minus effective rainfall.
          </div>
        </section>

        {/* ── B. Irrigation Schedule ── */}
        <section style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2ead8", padding: 20, marginBottom: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: "#1a2e1a", margin: 0 }}>Irrigation Schedule</h3>
            <div style={{ display: "flex", gap: 6 }}>
              {["monthly", "daily"].map(v => (
                <button key={v} onClick={() => setScheduleView(v)} style={{
                  padding: "5px 14px", fontSize: 12, fontWeight: 600, borderRadius: 6, cursor: "pointer",
                  background: scheduleView === v ? "#2d5a1b" : "#f0f6ea",
                  color: scheduleView === v ? "#fff" : "#2d5a1b",
                  border: "1px solid " + (scheduleView === v ? "#2d5a1b" : "#dde8d0"),
                }}>
                  {v === "monthly" ? "Monthly (Climate)" : "7-Day Forecast"}
                </button>
              ))}
            </div>
          </div>

          {scheduleView === "monthly" && (
            <div>
              <div style={{ fontSize: 12, color: "#7a9a6a", marginBottom: 10 }}>
                Based on 30-year climate normals (NASA POWER). Irrigate when net need exceeds 0 during your growing season.
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: "#f0f6ea" }}>
                      {["Month","Rain","ETc","Net Need","Volume for Field"].map(h => (
                        <th key={h} style={{ padding: "7px 10px", textAlign: "right", fontWeight: 700, fontSize: 11,
                          color: "#374151", borderBottom: "1px solid #e2ead8", whiteSpace: "nowrap",
                          textAlign: h === "Month" ? "left" : "right" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.monthly.map(mo => (
                      <tr key={mo.month} style={{ borderBottom: "1px solid #f0f0f0" }}>
                        <td style={{ padding: "5px 10px", fontWeight: 600, color: "#1a2e1a" }}>{mo.month}</td>
                        <td style={{ padding: "5px 10px", textAlign: "right", color: "#2563eb" }}>{mo.precip_mm} mm</td>
                        <td style={{ padding: "5px 10px", textAlign: "right", color: "#374151" }}>{mo.etc_mm} mm</td>
                        <NetCell mm={mo.net_mm} />
                        <td style={{ padding: "5px 10px", textAlign: "right", color: "#374151" }}>
                          {mo.net_mm > 0 ? `${mo.net_m3.toLocaleString()} m³` : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ fontSize: 12, color: "#7a9a6a", marginTop: 12, padding: "10px 14px",
                background: "#f0f6ea", borderRadius: 8 }}>
                <strong>Time of day:</strong> Irrigate early morning (5–8 AM) to minimize evaporation losses — typically 20–30% more efficient than midday irrigation. Avoid evening irrigation to reduce disease pressure.
              </div>
            </div>
          )}

          {scheduleView === "daily" && <DailySchedule />}
        </section>

        {/* ── C. Location Suitability ── */}
        <section style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2ead8", padding: 20 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: "#1a2e1a", marginBottom: 14 }}>Crop Irrigation Suitability</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <SuitCard suit={data.suitability} cropName={crop} />
            {data.compare_suitability && (
              <SuitCard suit={data.compare_suitability} cropName={compareCrop} />
            )}
          </div>
        </section>

      </>)}
    </div>
  );
}

// ── Shared SVG chart helpers ──────────────────────────────────────────────────
function ChartWrap({ title, sub, children }) {
  return (
    <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e2ead8", padding:"18px 20px", marginBottom:20 }}>
      <div style={{ fontSize:14, fontWeight:700, color:"#1a2e1a", marginBottom:2 }}>{title}</div>
      {sub && <div style={{ fontSize:11, color:"#7a9a6a", marginBottom:12 }}>{sub}</div>}
      {children}
    </div>
  );
}

function MiniBarInline({ value, max, color="#2d5a1b" }) {
  const w = max > 0 ? Math.min(100, value / max * 100) : 0;
  return (
    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
      <div style={{ flex:1, height:6, background:"#e8f0e0", borderRadius:3, overflow:"hidden" }}>
        <div style={{ width:`${w}%`, height:"100%", background:color, borderRadius:3 }} />
      </div>
      <span style={{ fontSize:11, color:"#374151", minWidth:32, textAlign:"right" }}>{value?.toFixed?.(0) ?? value}</span>
    </div>
  );
}

// Dual-axis SVG: rainfall bars (left) + temp lines (right)
function ClimateTrendSvg({ precip_mm, t_max, t_min, cropTMin, cropTMax }) {
  const W=560, H=180, PL=44, PR=44, PT=14, PB=30;
  const iW = W-PL-PR, iH = H-PT-PB;
  const n = 12;
  const barW = iW/n*0.6;
  const maxRain = Math.max(...precip_mm, 10);
  const allTemps = [...t_max, ...t_min];
  const tMin = Math.min(...allTemps) - 2, tMax = Math.max(...allTemps) + 2;
  const xOf  = i => PL + (i+0.5) * (iW/n);
  const yRain = v => PT + iH - (v/maxRain)*iH;
  const yTemp = v => PT + iH - ((v-tMin)/(tMax-tMin))*iH;
  const maxLine = t_max.map((v,i) => `${xOf(i)},${yTemp(v)}`).join(" ");
  const minLine = t_min.map((v,i) => `${xOf(i)},${yTemp(v)}`).join(" ");
  // Crop optimal band
  const bandY1 = Math.max(PT, yTemp(Math.min(cropTMax ?? tMax, tMax)));
  const bandY2 = Math.min(PT+iH, yTemp(Math.max(cropTMin ?? tMin, tMin)));
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", height:H, display:"block" }}>
      {/* Crop optimal band */}
      {cropTMin != null && cropTMax != null && (
        <rect x={PL} y={bandY1} width={iW} height={Math.max(0,bandY2-bandY1)}
          fill="rgba(45,90,27,0.08)" />
      )}
      {/* Rain bars */}
      {precip_mm.map((v,i) => (
        <rect key={i} x={xOf(i)-barW/2} y={yRain(v)} width={barW} height={PT+iH-yRain(v)}
          fill="rgba(37,99,235,0.5)" rx={2} />
      ))}
      {/* Temp lines */}
      <polyline points={maxLine} fill="none" stroke="#dc2626" strokeWidth={2} strokeLinejoin="round" />
      <polyline points={minLine} fill="none" stroke="#2563eb" strokeWidth={2} strokeLinejoin="round" />
      {/* X labels */}
      {MONTHS.map((m,i) => <text key={i} x={xOf(i)} y={H-PT+14} fontSize={8} fill="#888" textAnchor="middle">{m}</text>)}
      {/* Left Y axis (rain) */}
      {[0, Math.round(maxRain/2), Math.round(maxRain)].map(v => (
        <text key={v} x={PL-4} y={yRain(v)+3} fontSize={8} fill="#2563eb" textAnchor="end">{v}</text>
      ))}
      {/* Right Y axis (temp) */}
      {[Math.round(tMin), Math.round((tMin+tMax)/2), Math.round(tMax)].map(v => (
        <text key={v} x={W-PR+4} y={yTemp(v)+3} fontSize={8} fill="#dc2626" textAnchor="start">{v}°</text>
      ))}
      {/* Legend */}
      <rect x={PL} y={H-PT+16} width={8} height={6} fill="rgba(37,99,235,0.5)" />
      <text x={PL+11} y={H-PT+22} fontSize={8} fill="#555">Rainfall (mm)</text>
      <line x1={PL+80} y1={H-PT+19} x2={PL+92} y2={H-PT+19} stroke="#dc2626" strokeWidth={2} />
      <text x={PL+95} y={H-PT+22} fontSize={8} fill="#555">T max</text>
      <line x1={PL+130} y1={H-PT+19} x2={PL+142} y2={H-PT+19} stroke="#2563eb" strokeWidth={2} />
      <text x={PL+145} y={H-PT+22} fontSize={8} fill="#555">T min</text>
      {cropTMin != null && (
        <><rect x={PL+180} y={H-PT+15} width={8} height={8} fill="rgba(45,90,27,0.2)" />
        <text x={PL+191} y={H-PT+22} fontSize={8} fill="#2d5a1b">Crop range</text></>
      )}
    </svg>
  );
}

// Water budget stacked bars
function WaterBudgetSvg({ months }) {
  if (!months.length) return null;
  const W=560, H=160, PL=44, PR=16, PT=14, PB=30;
  const iW=W-PL-PR, iH=H-PT-PB, n=months.length;
  const barW = iW/n*0.7;
  const maxVal = Math.max(...months.map(m => (m.eta||0)*25.4), 10);
  const xOf = i => PL + (i+0.5)*(iW/n);
  const yOf = v => PT + iH - (v/maxVal)*iH;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", height:H, display:"block" }}>
      {months.map((m,i) => {
        const greenMm = +(m.green*25.4).toFixed(1);
        const blueMm  = +(m.blue *25.4).toFixed(1);
        const greenH  = (greenMm/maxVal)*iH;
        const blueH   = (blueMm /maxVal)*iH;
        const base    = PT+iH;
        return (
          <g key={i}>
            <rect x={xOf(i)-barW/2} y={base-greenH} width={barW} height={greenH} fill="rgba(74,158,47,0.7)" rx={2} />
            <rect x={xOf(i)-barW/2} y={base-greenH-blueH} width={barW} height={blueH} fill="rgba(37,99,235,0.65)" rx={2} />
          </g>
        );
      })}
      {months.map((m,i) => (
        <text key={i} x={xOf(i)} y={H-PT+14} fontSize={8} fill="#888" textAnchor="middle">{m.name}</text>
      ))}
      {[0,Math.round(maxVal/2),Math.round(maxVal)].map(v => (
        <text key={v} x={PL-4} y={yOf(v)+3} fontSize={8} fill="#555" textAnchor="end">{v}</text>
      ))}
      <rect x={PL} y={H-PT+16} width={8} height={6} fill="rgba(74,158,47,0.7)" />
      <text x={PL+11} y={H-PT+22} fontSize={8} fill="#555">Rain-met (mm)</text>
      <rect x={PL+90} y={H-PT+16} width={8} height={6} fill="rgba(37,99,235,0.65)" />
      <text x={PL+101} y={H-PT+22} fontSize={8} fill="#555">Irrigation gap (mm)</text>
    </svg>
  );
}

const FIELD_COLORS = ["#2d5a1b","#2563eb","#dc2626","#d97706","#7c3aed","#0891b2","#be185d","#15803d"];

// NDVI time series — supports multi-field (separate line per unique field name)
function NdviTimeSeriesSvg({ series, singleField = false }) {
  if (!series.length) return (
    <div style={{ color:"#aaa", fontSize:12, padding:"20px 0" }}>No NDVI data available.</div>
  );
  const W=560, H=160, PL=44, PR=singleField ? 16 : 120, PT=14, PB=36;
  const iW=W-PL-PR, iH=H-PT-PB;

  const sorted = [...series].sort((a,b) => a.date < b.date ? -1 : 1);
  const allDates = [...new Set(sorted.map(p => p.date))].sort();
  const n = allDates.length;
  const xOf = d => PL + (n > 1 ? allDates.indexOf(d) / (n-1) : 0.5) * iW;
  const yOf = v => PT + iH - v * iH;
  const ndviColor = v => v >= 0.6 ? "#16a34a" : v >= 0.4 ? "#ca8a04" : "#dc2626";

  // Group by field name (undefined → all in one group)
  const fields = singleField
    ? [{ name: null, pts: sorted, color: "#2d5a1b" }]
    : [...new Set(sorted.map(p => p.field || ""))].map((name, i) => ({
        name,
        pts: sorted.filter(p => (p.field || "") === name),
        color: FIELD_COLORS[i % FIELD_COLORS.length],
      }));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", height:H, display:"block" }}>
      {/* Grid lines */}
      {[0,0.2,0.4,0.6,0.8,1].map(v => (
        <g key={v}>
          <line x1={PL} y1={yOf(v)} x2={PL+iW} y2={yOf(v)} stroke="rgba(0,0,0,0.05)" strokeWidth={1} />
          <text x={PL-4} y={yOf(v)+3} fontSize={8} fill="#bbb" textAnchor="end">{v.toFixed(1)}</text>
        </g>
      ))}
      {/* Lines per field */}
      {fields.map(({ name, pts, color }) => {
        const linePts = pts.map(p => `${xOf(p.date)},${yOf(p.ndvi)}`).join(" ");
        return (
          <g key={name ?? "all"}>
            {pts.length > 1 && (
              <polyline points={linePts} fill="none" stroke={color} strokeWidth={2}
                strokeLinejoin="round" opacity={0.7} />
            )}
            {pts.map((p, i) => {
              const cx = xOf(p.date), cy = yOf(p.ndvi);
              const dotColor = singleField ? ndviColor(p.ndvi) : color;
              const r = p.isAnalysis ? 6 : 4;
              return (
                <g key={i}>
                  {p.isAnalysis && (
                    <circle cx={cx} cy={cy} r={r+3} fill="none" stroke={dotColor} strokeWidth={1.5} opacity={0.4} />
                  )}
                  <circle cx={cx} cy={cy} r={r} fill={dotColor} stroke="#fff" strokeWidth={1.5} />
                  <text x={cx} y={cy-9} fontSize={8} fill={dotColor}
                    textAnchor="middle" fontWeight="700">
                    {Number(p.ndvi).toFixed(2)}
                  </text>
                </g>
              );
            })}
          </g>
        );
      })}
      {/* X date labels */}
      {allDates.map((d, i) => (
        <text key={d} x={xOf(d)} y={H-PB+14} fontSize={7.5} fill="#888"
          textAnchor="middle" transform={`rotate(-35,${xOf(d)},${H-PB+14})`}>{d}</text>
      ))}
      {/* Legend (multi-field only) */}
      {!singleField && fields.length > 1 && fields.map(({ name, color }, i) => (
        <g key={name} transform={`translate(${W-PR+8}, ${PT + i*16})`}>
          <circle cx={5} cy={5} r={4} fill={color} />
          <text x={13} y={9} fontSize={8} fill="#374151">
            {name ? (name.length > 14 ? name.slice(0,13)+"…" : name) : "?"}
          </text>
        </g>
      ))}
    </svg>
  );
}

// Radar / spider chart — 5 axes
function RadarSvg({ axes }) {
  // axes: [{ label, score: 0-100, color? }]
  const CX=140, CY=110, R=80, W=280, H=220;
  const n = axes.length;
  const angle = i => (Math.PI*2/n)*i - Math.PI/2;
  const pt = (i, r) => [CX + Math.cos(angle(i))*r, CY + Math.sin(angle(i))*r];
  // Grid rings
  const rings = [20,40,60,80,100];
  const scorePts = axes.map((a,i) => pt(i, a.score*R/100));
  const polyPts = scorePts.map(p => p.join(",")).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", maxWidth:320, height:H, display:"block", margin:"0 auto" }}>
      {/* Rings */}
      {rings.map(r => (
        <polygon key={r} points={axes.map((_,i) => pt(i, r*R/100).join(",")).join(" ")}
          fill="none" stroke="#e2ead8" strokeWidth={1} />
      ))}
      {/* Spokes */}
      {axes.map((_,i) => (
        <line key={i} x1={CX} y1={CY} x2={pt(i,R)[0]} y2={pt(i,R)[1]} stroke="#e2ead8" strokeWidth={1} />
      ))}
      {/* Score polygon */}
      <polygon points={polyPts} fill="rgba(45,90,27,0.18)" stroke="#2d5a1b" strokeWidth={2} />
      {/* Score dots */}
      {scorePts.map((p,i) => (
        <circle key={i} cx={p[0]} cy={p[1]} r={4} fill="#2d5a1b" />
      ))}
      {/* Labels */}
      {axes.map((a,i) => {
        const [lx, ly] = pt(i, R+18);
        return (
          <g key={i}>
            <text x={lx} y={ly} fontSize={9} fill="#374151" textAnchor="middle" fontWeight="600">{a.label}</text>
            <text x={lx} y={ly+11} fontSize={8} fill="#7a9a6a" textAnchor="middle">{a.score}</text>
          </g>
        );
      })}
      {/* Center ring labels */}
      {[20,60,100].map(v => (
        <text key={v} x={CX+3} y={CY - v*R/100 + 3} fontSize={7} fill="#bbb">{v}</text>
      ))}
    </svg>
  );
}

// ── Charts Tab ────────────────────────────────────────────────────────────────
// Simplified crop requirement lookup (mirrors backend _CROP_REQUIREMENTS)
// Keyed by IRRIG_CROPS key → display name + climate requirements
// Covers all 98 OpenFarm-supported crops (FAO-56 list)
const CROP_REQ_FE = {
  // ── Vegetables ──
  artichoke:        {name:"Artichoke",                 t_min:7,  t_max:28, r_min:400, r_max:700},
  asparagus:        {name:"Asparagus",                 t_min:5,  t_max:30, r_min:300, r_max:600},
  beet_table:       {name:"Beet (table)",              t_min:4,  t_max:28, r_min:350, r_max:600},
  broccoli:         {name:"Broccoli",                  t_min:5,  t_max:22, r_min:400, r_max:600},
  brussels_sprouts: {name:"Brussels Sprouts",          t_min:4,  t_max:20, r_min:400, r_max:600},
  cabbage:          {name:"Cabbage",                   t_min:5,  t_max:24, r_min:380, r_max:500},
  cantaloupe:       {name:"Cantaloupe",                t_min:18, t_max:35, r_min:400, r_max:600},
  carrot:           {name:"Carrot",                    t_min:5,  t_max:28, r_min:350, r_max:600},
  cauliflower:      {name:"Cauliflower",               t_min:5,  t_max:22, r_min:400, r_max:600},
  celery:           {name:"Celery",                    t_min:5,  t_max:25, r_min:500, r_max:700},
  cucumber:         {name:"Cucumber",                  t_min:15, t_max:35, r_min:500, r_max:700},
  eggplant:         {name:"Eggplant",                  t_min:15, t_max:35, r_min:500, r_max:700},
  garlic:           {name:"Garlic",                    t_min:5,  t_max:30, r_min:250, r_max:500},
  kale:             {name:"Kale",                      t_min:5,  t_max:25, r_min:300, r_max:500},
  leek:             {name:"Leek",                      t_min:5,  t_max:28, r_min:350, r_max:600},
  lettuce:          {name:"Lettuce",                   t_min:4,  t_max:24, r_min:150, r_max:300},
  okra:             {name:"Okra",                      t_min:18, t_max:38, r_min:500, r_max:900},
  onion_dry:        {name:"Onion (dry)",               t_min:5,  t_max:30, r_min:350, r_max:550},
  onion_green:      {name:"Onion (green)",             t_min:5,  t_max:30, r_min:350, r_max:550},
  peas_fresh:       {name:"Peas (fresh)",              t_min:4,  t_max:24, r_min:350, r_max:600},
  pepper:           {name:"Sweet Pepper",              t_min:12, t_max:35, r_min:600, r_max:1200},
  pumpkin:          {name:"Pumpkin / Winter Squash",   t_min:15, t_max:35, r_min:400, r_max:700},
  radish:           {name:"Radish",                    t_min:5,  t_max:25, r_min:250, r_max:450},
  spinach:          {name:"Spinach",                   t_min:5,  t_max:25, r_min:300, r_max:500},
  squash:           {name:"Squash (summer/zucchini)",  t_min:15, t_max:35, r_min:400, r_max:700},
  sweet_corn:       {name:"Sweet Corn",                t_min:10, t_max:35, r_min:500, r_max:800},
  swiss_chard:      {name:"Swiss Chard",               t_min:5,  t_max:28, r_min:300, r_max:550},
  tomato_fresh:     {name:"Tomato (fresh)",            t_min:10, t_max:35, r_min:400, r_max:600},
  tomato_proc:      {name:"Tomato (processing)",       t_min:10, t_max:35, r_min:400, r_max:600},
  turnip:           {name:"Turnip",                    t_min:4,  t_max:25, r_min:250, r_max:500},
  watermelon:       {name:"Watermelon",                t_min:18, t_max:38, r_min:400, r_max:600},
  // ── Root / Tubers ──
  cassava:          {name:"Cassava",                   t_min:16, t_max:38, r_min:500, r_max:1500},
  potato:           {name:"Potato",                    t_min:5,  t_max:25, r_min:500, r_max:700},
  sweet_potato:     {name:"Sweet Potato",              t_min:15, t_max:35, r_min:750, r_max:1500},
  yam:              {name:"Yam",                       t_min:18, t_max:35, r_min:1000,r_max:1500},
  // ── Legumes ──
  bean_dry:         {name:"Bean (dry)",                t_min:10, t_max:30, r_min:300, r_max:600},
  bean_green:       {name:"Bean (green/snap)",         t_min:10, t_max:30, r_min:300, r_max:600},
  chickpea:         {name:"Chickpea",                  t_min:5,  t_max:32, r_min:300, r_max:650},
  cowpea:           {name:"Cowpea",                    t_min:18, t_max:38, r_min:400, r_max:700},
  fava_bean:        {name:"Fava Bean",                 t_min:4,  t_max:28, r_min:350, r_max:700},
  groundnut:        {name:"Groundnut / Peanut",        t_min:20, t_max:35, r_min:500, r_max:1200},
  lentil:           {name:"Lentil",                    t_min:5,  t_max:30, r_min:250, r_max:500},
  soybean:          {name:"Soybean",                   t_min:10, t_max:35, r_min:450, r_max:850},
  // ── Cereals ──
  barley:           {name:"Barley",                    t_min:2,  t_max:30, r_min:200, r_max:1000},
  corn_maize:       {name:"Corn / Maize",              t_min:10, t_max:35, r_min:500, r_max:800},
  millet:           {name:"Millet",                    t_min:15, t_max:40, r_min:200, r_max:600},
  oat:              {name:"Oat",                       t_min:2,  t_max:28, r_min:250, r_max:750},
  quinoa:           {name:"Quinoa",                    t_min:4,  t_max:32, r_min:250, r_max:600},
  rice:             {name:"Rice (paddy)",               t_min:16, t_max:38, r_min:1000,r_max:2000},
  rye:              {name:"Rye",                       t_min:-5, t_max:25, r_min:250, r_max:800},
  sorghum:          {name:"Sorghum",                   t_min:10, t_max:40, r_min:300, r_max:650},
  wheat_winter:     {name:"Wheat (winter)",            t_min:-5, t_max:25, r_min:250, r_max:900},
  wheat_spring:     {name:"Wheat (spring)",            t_min:3,  t_max:30, r_min:250, r_max:750},
  // ── Oil Crops ──
  canola:           {name:"Canola / Rapeseed",         t_min:3,  t_max:28, r_min:300, r_max:700},
  cotton:           {name:"Cotton",                    t_min:15, t_max:38, r_min:700, r_max:1300},
  flax:             {name:"Flax / Linseed",            t_min:3,  t_max:28, r_min:250, r_max:600},
  safflower:        {name:"Safflower",                 t_min:7,  t_max:38, r_min:250, r_max:600},
  sesame:           {name:"Sesame",                    t_min:18, t_max:38, r_min:300, r_max:600},
  sunflower:        {name:"Sunflower",                 t_min:10, t_max:34, r_min:300, r_max:700},
  // ── Sugar ──
  sugarcane:        {name:"Sugarcane",                 t_min:18, t_max:38, r_min:1500,r_max:2500},
  sugar_beet:       {name:"Sugar Beet",                t_min:4,  t_max:28, r_min:400, r_max:700},
  // ── Tropical Fruits ──
  avocado:          {name:"Avocado",                   t_min:10, t_max:30, r_min:1000,r_max:1600},
  banana:           {name:"Banana",                    t_min:15, t_max:35, r_min:1200,r_max:2500},
  citrus_orange:    {name:"Citrus (orange)",           t_min:8,  t_max:35, r_min:600, r_max:1200},
  citrus_lemon:     {name:"Citrus (lemon/lime)",       t_min:5,  t_max:35, r_min:500, r_max:1200},
  coconut:          {name:"Coconut",                   t_min:20, t_max:38, r_min:1300,r_max:2500},
  coffee:           {name:"Coffee",                    t_min:15, t_max:28, r_min:1200,r_max:2200},
  mango:            {name:"Mango",                     t_min:18, t_max:38, r_min:500, r_max:1000},
  papaya:           {name:"Papaya",                    t_min:18, t_max:38, r_min:1000,r_max:2000},
  pineapple:        {name:"Pineapple",                 t_min:18, t_max:35, r_min:1000,r_max:1500},
  tea:              {name:"Tea",                       t_min:13, t_max:29, r_min:1150,r_max:2500},
  // ── Deciduous Fruits / Nuts ──
  almond:           {name:"Almond",                    t_min:5,  t_max:35, r_min:250, r_max:600},
  apple:            {name:"Apple",                     t_min:-5, t_max:30, r_min:600, r_max:1200},
  apricot:          {name:"Apricot",                   t_min:-5, t_max:32, r_min:500, r_max:900},
  cherry:           {name:"Cherry",                    t_min:-5, t_max:28, r_min:600, r_max:1000},
  olive:            {name:"Olive",                     t_min:5,  t_max:38, r_min:250, r_max:700},
  peach:            {name:"Peach / Nectarine",         t_min:-5, t_max:32, r_min:500, r_max:900},
  pear:             {name:"Pear",                      t_min:-5, t_max:28, r_min:600, r_max:1000},
  pistachio:        {name:"Pistachio",                 t_min:5,  t_max:40, r_min:200, r_max:600},
  plum:             {name:"Plum / Prune",              t_min:-5, t_max:30, r_min:600, r_max:1000},
  walnut:           {name:"Walnut",                    t_min:4,  t_max:32, r_min:500, r_max:1000},
  // ── Berries / Vines ──
  blueberry:        {name:"Blueberry",                 t_min:3,  t_max:28, r_min:700, r_max:1200},
  grape_table:      {name:"Grape (table)",             t_min:5,  t_max:35, r_min:350, r_max:600},
  grape_wine:       {name:"Grape (wine)",              t_min:5,  t_max:35, r_min:350, r_max:600},
  raspberry:        {name:"Raspberry",                 t_min:3,  t_max:25, r_min:500, r_max:900},
  strawberry:       {name:"Strawberry",                t_min:5,  t_max:28, r_min:600, r_max:1000},
  // ── Forages ──
  alfalfa:          {name:"Alfalfa (multi-cut)",       t_min:5,  t_max:32, r_min:400, r_max:800},
  bermuda_grass:    {name:"Bermuda Grass",             t_min:15, t_max:38, r_min:600, r_max:1200},
  clover:           {name:"Clover",                    t_min:5,  t_max:28, r_min:500, r_max:900},
  ryegrass:         {name:"Ryegrass / Pasture",        t_min:5,  t_max:25, r_min:500, r_max:900},
  // ── Specialty / Herbs ──
  basil:            {name:"Basil",                     t_min:12, t_max:35, r_min:300, r_max:600},
  cilantro:         {name:"Cilantro / Coriander",      t_min:5,  t_max:28, r_min:250, r_max:500},
  hemp:             {name:"Hemp / Cannabis",            t_min:10, t_max:32, r_min:300, r_max:700},
  hops:             {name:"Hops",                      t_min:5,  t_max:28, r_min:500, r_max:900},
  mint:             {name:"Mint",                      t_min:5,  t_max:30, r_min:400, r_max:700},
  tobacco:          {name:"Tobacco",                   t_min:13, t_max:35, r_min:400, r_max:800},
};

// Normalize a stored crop string (may be a key, a display name, or free text) to a CROP_REQ_FE key
function normalizeCropKey(raw) {
  if (!raw) return "";
  const s = String(raw).trim();
  // Exact key match
  if (CROP_REQ_FE[s]) return s;
  // Case-insensitive key match
  const lower = s.toLowerCase();
  const byKey = Object.keys(CROP_REQ_FE).find(k => k.toLowerCase() === lower);
  if (byKey) return byKey;
  // Match against display name
  const byName = Object.keys(CROP_REQ_FE).find(k =>
    CROP_REQ_FE[k].name.toLowerCase() === lower ||
    CROP_REQ_FE[k].name.toLowerCase().includes(lower) ||
    lower.includes(CROP_REQ_FE[k].name.toLowerCase().split(" ")[0])
  );
  return byName || "";
}

function getCentroid(polygon) {
  const coords = Array.isArray(polygon) ? polygon
    : polygon?.coordinates?.[0] || polygon?.geometry?.coordinates?.[0];
  if (!coords || coords.length < 3) return null;
  return {
    lat: coords.reduce((s,p) => s+p[1], 0) / coords.length,
    lon: coords.reduce((s,p) => s+p[0], 0) / coords.length,
  };
}

function parseResult(raw) {
  if (!raw) return null;
  if (typeof raw === "string") { try { return JSON.parse(raw); } catch { return null; } }
  return raw;
}

function portfolioScoreFromResult(raw) {
  const result = parseResult(raw);
  if (!result) return null;
  // Try health_status first (covers GEE-based analyses)
  if (result.health_status) {
    const hs = result.health_status.toLowerCase();
    const m = {
      "good":85, "excellent":95, "fair":60, "moderate stress":40,
      "poor":30, "severe stress":15, "critical":10, "unknown":null,
    };
    const v = m[hs];
    if (v != null) return v;
    // Fall through to NDVI if health_status isn't in the map
  }
  const ndvi = result.ndvi ?? result.mean_ndvi;
  if (ndvi != null) return Math.round(Number(ndvi) * 100);
  return null;
}

// Canonical crop name from a row: top-level crop column first, then result fields
function rowCrop(a) {
  if (!a) return "";
  const r = parseResult(a.result);
  return a.crop || r?.crop || r?.inputs?.crop || r?.crop_type || "";
}

// Geodetic polygon area in hectares using the spherical excess formula
function polygonAreaHa(coords) {
  if (!coords || coords.length < 3) return null;
  const R = 6371000; // Earth radius in metres
  const toRad = d => d * Math.PI / 180;
  let area = 0;
  const n = coords.length;
  for (let i = 0; i < n; i++) {
    const [lon1, lat1] = coords[i];
    const [lon2, lat2] = coords[(i + 1) % n];
    area += toRad(lon2 - lon1) * (2 + Math.sin(toRad(lat1)) + Math.sin(toRad(lat2)));
  }
  return Math.abs(area * R * R / 2) / 10000; // m² → ha
}

// Canonical area (ha) from a row — compute from polygon if not stored
function rowArea(a) {
  if (!a) return null;
  const r = parseResult(a.result);
  const stored = a.area_ha ?? r?.area_ha ?? r?.field_area_ha ?? r?.inputs?.area_ha ?? null;
  if (stored != null) return stored;
  // Fall back to computing from polygon
  const coords = Array.isArray(a.polygon) ? a.polygon
    : a.polygon?.coordinates?.[0] || a.polygon?.geometry?.coordinates?.[0];
  return polygonAreaHa(coords);
}

// Canonical NDVI from a row
function rowNdvi(a) {
  if (!a) return null;
  const r = parseResult(a.result);
  return r?.ndvi ?? r?.mean_ndvi ?? null;
}

function ChartsTab({ org, lang = "en" }) {
  const { t } = useTranslation();
  const [analyses, setAnalyses]     = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [climate, setClimate]       = useState(null);
  const [climLoading, setClimLoading] = useState(false);
  const [cropOverride, setCropOverride] = useState("");
  const [ndviHistory, setNdviHistory]   = useState([]);   // fetched from /ndvi-history
  const [ndviLoading, setNdviLoading]   = useState(false);
  const headers = { Authorization: `Bearer ${getToken()}` };

  useEffect(() => {
    if (!org) return;
    axios.get(`${API}/analyses/org/${org.id}`, { headers })
      .then(r => {
        const list = Array.isArray(r.data) ? r.data : (r.data?.analyses || []);
        setAnalyses(list);
        if (list.length) setSelectedId(String(list[0].id));
      }).catch(() => {});
  }, [org?.id]);

  const selected = analyses.find(a => String(a.id) === selectedId);

  // Reset crop override and ndvi history when field changes
  useEffect(() => { setCropOverride(""); setNdviHistory([]); }, [selectedId]);

  // Fetch climate when selected field changes
  useEffect(() => {
    if (!selected) return;
    const c = getCentroid(selected.polygon);
    if (!c) { setClimate(null); return; }
    setClimLoading(true);
    setClimate(null);
    axios.post(`${API}/irrigation-climate`, { lat: c.lat, lon: c.lon })
      .then(r => { setClimate(r.data); setClimLoading(false); })
      .catch(() => setClimLoading(false));
  }, [selectedId]);

  // When per-field NDVI has ≤1 point, fetch 12 months from GEE then cache back to DB
  useEffect(() => {
    if (!selected) return;
    const result = parseResult(selected.result);
    const existingSeries = result?.ndvi_series || [];
    // Already have ≥2 points — no need to fetch
    if (existingSeries.length > 1) { setNdviHistory([]); return; }
    const coords = Array.isArray(selected.polygon) ? selected.polygon
      : selected.polygon?.coordinates?.[0] || selected.polygon?.geometry?.coordinates?.[0];
    if (!coords || coords.length < 3) return;
    setNdviLoading(true);
    axios.post(`${API}/ndvi-history`, { coordinates: coords, months: 12 })
      .then(r => {
        const fetched = r.data?.series || [];
        setNdviHistory(fetched);
        setNdviLoading(false);
        if (!fetched.length) return;
        // Merge with existing single point and save back to DB
        const toSave = [...existingSeries, ...fetched];
        axios.post(`${API}/analyses/patch-ndvi-series`,
          { analysis_id: selected.id, series: toSave },
          { headers }
        ).then(() => {
          // Update local analyses list so future tab switches don't re-fetch
          setAnalyses(prev => prev.map(a => {
            if (a.id !== selected.id) return a;
            const r2 = parseResult(a.result) || {};
            const existingMonths = new Set((r2.ndvi_series || []).map(p => p.date?.slice(0,7)));
            const merged = [...(r2.ndvi_series || [])];
            toSave.forEach(p => { if (!existingMonths.has(p.date?.slice(0,7))) merged.push(p); });
            merged.sort((a,b) => a.date < b.date ? -1 : 1);
            return { ...a, result: { ...r2, ndvi_series: merged } };
          }));
        }).catch(() => {});
      })
      .catch(() => setNdviLoading(false));
  }, [selectedId]);

  const crop = cropOverride || normalizeCropKey(rowCrop(selected)) || "";
  const req  = CROP_REQ_FE[crop] || null;

  // Radar scores — all axes 0–100
  function radarScores(climate, req, a) {
    if (!climate || !req) return null;

    const tMean      = climate.t_mean  || [];
    const precip_mm  = climate.precip_mm || [];
    const eto_in     = climate.eto || [];   // inches/month from NASA POWER
    const annualRain = precip_mm.reduce((s,v) => s + v, 0);
    const annualEto  = eto_in.reduce((s,v) => s + v * 25.4, 0);   // → mm

    // ── 1. Temperature Fit ──────────────────────────────────────────────────
    // % of months where mean temp is within [t_min, t_max]; partial credit for
    // months just outside (±5°C) so the score doesn't cliff-edge to 0.
    let tempPts = 0;
    tMean.forEach(t => {
      if (t >= req.t_min && t <= req.t_max)          tempPts += 1.0;
      else if (t >= req.t_min - 5 && t < req.t_min)  tempPts += 0.3;
      else if (t > req.t_max && t <= req.t_max + 5)  tempPts += 0.3;
    });
    const tempScore = Math.min(100, Math.round(tempPts / 12 * 100));

    // ── 2. Rain Coverage — what % of crop ETc is met by rainfall ───────────
    const cropDef      = IRRIG_CROPS[crop] || null;
    const kcMid        = cropDef ? cropDef.kc[1] : 1.0;
    const annualEtc    = annualEto * kcMid;
    const rainCoverage = annualEtc > 0 ? Math.min(100, Math.round(annualRain / annualEtc * 100)) : 50;
    const irrGap       = Math.max(0, annualEtc - annualRain);

    // ── 3. Field Health ─────────────────────────────────────────────────────
    const healthScore = portfolioScoreFromResult(a?.result) ?? 50;

    // ── 4. Season Viability ─────────────────────────────────────────────────
    const growableMo  = tMean.filter(t => t >= req.t_min).length;
    const totalDays   = cropDef ? cropDef.L.reduce((s,v) => s + v, 0) : 150;
    const neededMo    = Math.max(1, Math.ceil(totalDays / 30));
    const seasonScore = Math.min(100, Math.round(growableMo / neededMo * 100));

    // ── 5. Soil Score ───────────────────────────────────────────────────────
    const result = parseResult(a?.result);
    const soilRaw = result?.soil;
    let soilScore = null;
    if (soilRaw && !soilRaw.error) {
      const pH   = soilRaw.ph_h2o ?? soilRaw.pH ?? null;
      const soc  = soilRaw.soc ?? soilRaw.SOC ?? null;
      const clay = soilRaw.clay ?? null;
      const sand = soilRaw.sand ?? null;
      let pts = 0, total = 0;
      if (pH != null)   { pts += Math.max(0, 100 - Math.abs(pH - 6.5) * 20); total += 100; }
      if (soc != null)  { pts += Math.min(100, soc * 20); total += 100; }
      if (clay != null && sand != null) {
        const loam = 100 - Math.abs(clay - 25) - Math.abs(sand - 45);
        pts += Math.max(0, loam); total += 100;
      }
      if (total > 0) soilScore = Math.round(pts / total * 100);
    }

    return [
      { label: "Temp Fit",      score: tempScore,    note: `${Math.round(tempPts)} of 12 mo suitable` },
      { label: "Rain Coverage", score: rainCoverage, note: irrGap === 0 ? `Rain fully covers crop ETc` : `${Math.round(annualRain)} mm rain / ${Math.round(annualEtc)} mm ETc needed` },
      { label: "Field Health",  score: healthScore,  note: "from NDVI / health status" },
      { label: "Season Viab.",  score: seasonScore,  note: `${growableMo} growable mo vs ${neededMo} needed` },
      { label: "Soil",          score: soilScore ?? 50, note: soilScore != null ? "pH, SOC & texture from SoilGrids" : "no soil data for this field" },
    ];
  }

  // Water budget
  function growingSeasonBudget(climate) {
    if (!climate) return [];
    return MONTHS.map((name, i) => {
      const eto_mm  = (climate.eto?.[i] || 0) * 25.4;
      const rain_mm = climate.precip_mm?.[i] || 0;
      const etc_mm  = eto_mm;
      const blue  = Math.max(0, etc_mm - rain_mm) / 25.4;
      const green = Math.min(etc_mm, rain_mm) / 25.4;
      return { name, eta: etc_mm / 25.4, blue, green };
    });
  }

  // NDVI series for selected field: GEE monthly series + actual analysis point + fetched history
  function ndviSeriesForField(a, history) {
    if (!a) return [];
    const result = parseResult(a.result);
    const pts = [];
    // Add GEE monthly composites
    if (result?.ndvi_series?.length) {
      result.ndvi_series.forEach(p => pts.push({ date: p.date?.slice(0,7), ndvi: +p.ndvi }));
    }
    // Always add the actual analysis NDVI (more precise, single-date) as its own point
    const actualNdvi = rowNdvi(a);
    if (actualNdvi != null) {
      const dt = (a.analysis_date || a.last_analyzed_at?.slice(0,10) || a.created_at?.slice(0,10) || "").slice(0,7);
      if (dt) pts.push({ date: dt, ndvi: +actualNdvi, isAnalysis: true });
    }
    // Add fetched satellite history, deduping by month (actual analysis point wins)
    const existingMonths = new Set(pts.map(p => p.date?.slice(0,7)));
    history.forEach(p => {
      if (!existingMonths.has(p.date?.slice(0,7))) pts.push({ date: p.date?.slice(0,7), ndvi: +p.ndvi });
    });
    return pts.filter(p => p.date).sort((a,b) => a.date < b.date ? -1 : 1);
  }

  const radar   = radarScores(climate, req, selected);
  const budget  = growingSeasonBudget(climate);
  const ndviPts = ndviSeriesForField(selected, ndviHistory);

  const sectionLabel = (text) => (
    <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em",
      color:"#7a9a6a", marginBottom:12, marginTop:4, display:"flex", alignItems:"center", gap:8 }}>
      <div style={{ flex:1, height:1, background:"#e2ead8" }} />
      {text}
      <div style={{ flex:1, height:1, background:"#e2ead8" }} />
    </div>
  );

  if (!analyses.length) return <div style={{ color:"#999", fontSize:13 }}>{t("noSavedAnalyses")}</div>;

  return (
    <div>
      <h2 style={{ fontSize:18, fontWeight:700, color:"#1a2e1a", marginBottom:8 }}>{t("chartsTab")}</h2>
      <p style={{ fontSize:12, color:"#7a9a6a", marginBottom:20 }}>
        {t("chartsSubtitle")}
      </p>

      {/* ── Per-field charts ── */}
      {sectionLabel(t("perFieldAnalysis"))}

      {/* Field selector */}
      <div style={{ marginBottom:20, display:"flex", alignItems:"center", gap:12 }}>
        <label style={{ fontSize:12, color:"#7a9a6a", fontWeight:600 }}>{t("fieldLabel")}</label>
        <select value={selectedId} onChange={e => setSelectedId(e.target.value)}
          style={{ padding:"6px 10px", fontSize:13, borderRadius:7, border:"1px solid #dde8d0", background:"#fff" }}>
          {analyses.map(a => {
            const c = rowCrop(a);
            return (
              <option key={a.id} value={a.id}>
                {a.name || `#${a.id}`}{c ? ` — ${c}` : ""}
              </option>
            );
          })}
        </select>
        {climLoading && <Sk w={80} h={18} r={5} />}
      </div>

      {/* 1. NDVI History — selected field only */}
      <ChartWrap title={`${t("chartNdviTitle")} — ${selected?.name || ""}`}
        sub={t("chartNdviSub")}>
        {ndviLoading
          ? <div style={{ paddingTop:8 }}><Sk w="100%" h={120} r={8} /></div>
          : <NdviTimeSeriesSvg series={ndviPts} singleField />
        }
      </ChartWrap>

      {/* 2. Climate Trend — selected field */}
      {climate && (
        <ChartWrap title={`${t("chartClimateTitle")} — ${selected?.name || ""}`}
          sub={t("chartClimateSub")}>
          <ClimateTrendSvg
            precip_mm={climate.precip_mm || []}
            t_max={climate.t_max || []}
            t_min={climate.t_min || []}
            cropTMin={req?.t_min}
            cropTMax={req?.t_max}
          />
        </ChartWrap>
      )}

      {/* 3. Suitability Radar — selected field + crop (crop selector lives here) */}
      {climate && (
        <ChartWrap title={t("chartRadarTitle")}
          sub={t("chartRadarSub")}>
          {/* Inline crop selector */}
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:16 }}>
            <label style={{ fontSize:12, color:"#7a9a6a", fontWeight:600 }}>{t("cropLabel")}</label>
            <select value={cropOverride || normalizeCropKey(rowCrop(selected))}
              onChange={e => setCropOverride(e.target.value)}
              style={{ padding:"6px 10px", fontSize:13, borderRadius:7, border:"1px solid #dde8d0", background:"#fff" }}>
              {!normalizeCropKey(rowCrop(selected)) && <option value="">— select crop —</option>}
              {Object.keys(CROP_REQ_FE).sort((a,b) => CROP_REQ_FE[a].name.localeCompare(CROP_REQ_FE[b].name))
                .map(k => <option key={k} value={k}>{CROP_REQ_FE[k].name}</option>)}
            </select>
          </div>
          {radar ? (
            <>
              <RadarSvg axes={radar} />
              <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginTop:12 }}>
                {radar.map(a => (
                  <div key={a.label} style={{ fontSize:11, color:"#374151", background:"#f0f6ea",
                    borderRadius:6, padding:"6px 10px", minWidth:120 }}>
                    <div><strong>{a.label}</strong>: {a.score}/100</div>
                    {a.note && <div style={{ fontSize:10, color:"#9ca3af", marginTop:2 }}>{a.note}</div>}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div style={{ fontSize:12, color:"#92400e" }}>{t("selectCropForRadar")}</div>
          )}
        </ChartWrap>
      )}

      {/* 5. Water Budget — selected field */}
      {climate && budget.length > 0 && (
        <ChartWrap title={`${t("chartWaterTitle")} — ${selected?.name || ""}${crop ? ` (${crop})` : ""}`}
          sub={t("chartWaterSub")}>
          <WaterBudgetSvg months={budget} />
        </ChartWrap>
      )}

      {/* ── Portfolio charts ── */}
      {sectionLabel(t("portfolioOverview"))}

      {/* 4. Multi-field Comparison */}
      <ChartWrap title={t("chartComparisonTitle")}
        sub={t("chartComparisonSub")}>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
            <thead>
              <tr style={{ background:"#f0f6ea" }}>
                {[t("tableField"),t("tableCrop"),t("tableArea"),t("tableHealthScore"),t("tableNdvi"),t("tableAnalysed")].map(h => (
                  <th key={h} style={{ padding:"7px 10px", textAlign:"left", fontWeight:700,
                    fontSize:11, color:"#374151", borderBottom:"1px solid #e2ead8" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...analyses]
                .map(a => ({ ...a, _score: portfolioScoreFromResult(a.result) ?? 0 }))
                .sort((a,b) => b._score - a._score)
                .map(a => {
                  const cropN = rowCrop(a) || "—";
                  const area  = rowArea(a);
                  const ndvi  = rowNdvi(a);
                  const date  = a.analysis_date || a.last_analyzed_at?.slice(0,10) || a.created_at?.slice(0,10) || "—";
                  const isSelected = String(a.id) === selectedId;
                  return (
                    <tr key={a.id}
                      onClick={() => setSelectedId(String(a.id))}
                      style={{ borderBottom:"1px solid #f0f0f0", cursor:"pointer",
                        background: isSelected ? "#f0f6ea" : "transparent" }}>
                      <td style={{ padding:"7px 10px", fontWeight:600, color:"#1a2e1a" }}>
                        {isSelected && <span style={{ marginRight:5, color:"#2d5a1b" }}>▶</span>}
                        {a.name || `#${a.id}`}
                      </td>
                      <td style={{ padding:"7px 10px", color:"#374151" }}>{tCrop(cropN, lang)}</td>
                      <td style={{ padding:"7px 10px", color:"#374151" }}>{area != null ? Number(area).toFixed(2) : "—"}</td>
                      <td style={{ padding:"7px 10px", minWidth:140 }}>
                        <MiniBarInline value={a._score} max={100}
                          color={a._score>=70?"#16a34a":a._score>=40?"#ca8a04":"#dc2626"} />
                      </td>
                      <td style={{ padding:"7px 10px",
                        color: ndvi!=null?(ndvi>=0.6?"#16a34a":ndvi>=0.4?"#ca8a04":"#dc2626"):"#aaa",
                        fontWeight: ndvi!=null ? 700 : 400 }}>
                        {ndvi != null ? Number(ndvi).toFixed(3) : "—"}
                      </td>
                      <td style={{ padding:"7px 10px", color:"#9ca3af" }}>{date}</td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </ChartWrap>

      {/* NDVI portfolio: separate line per field */}
      {(() => {
        const allPts = analyses.flatMap(a => {
          const result = parseResult(a.result);
          const fieldName = a.name || `#${a.id}`;
          const pts = [];
          // GEE monthly composites
          if (result?.ndvi_series?.length) {
            result.ndvi_series.forEach(p => pts.push({ date: p.date?.slice(0,7), ndvi: +p.ndvi, field: fieldName }));
          }
          // Always include the actual analysis NDVI point
          const actualNdvi = rowNdvi(a);
          if (actualNdvi != null) {
            const dt = (a.analysis_date || a.last_analyzed_at?.slice(0,10) || a.created_at?.slice(0,10) || "").slice(0,7);
            if (dt) {
              // Replace any same-month composite with the more precise analysis value
              const idx = pts.findIndex(p => p.date === dt);
              const pt = { date: dt, ndvi: +actualNdvi, field: fieldName, isAnalysis: true };
              if (idx >= 0) pts[idx] = pt; else pts.push(pt);
            }
          }
          return pts.filter(p => p.date);
        }).sort((a,b) => a.date < b.date ? -1 : 1);
        if (!allPts.length) return null;
        const uniqueFields = [...new Set(allPts.map(p => p.field))];
        return (
          <ChartWrap title={t("chartNdviAllTitle")}
            sub={t("chartNdviAllSub", { count: uniqueFields.length })}>
            <NdviTimeSeriesSvg series={allPts} />
          </ChartWrap>
        );
      })()}
    </div>
  );
}

// ── Recommendations Tab ───────────────────────────────────────────────────────
// Keyed by IRRIG_CROPS key (same as CROP_REQ_FE key)
const ROTATION_FAMILY = {
  // Vegetables → fruiting or brassica
  artichoke:"brassica", asparagus:"fruiting", beet_table:"root",
  broccoli:"brassica", brussels_sprouts:"brassica", cabbage:"brassica",
  cantaloupe:"fruiting", carrot:"root", cauliflower:"brassica",
  celery:"fruiting", cucumber:"fruiting", eggplant:"fruiting",
  garlic:"root", kale:"brassica", leek:"root", lettuce:"brassica",
  okra:"fruiting", onion_dry:"root", onion_green:"root", peas_fresh:"legume",
  pepper:"fruiting", pumpkin:"fruiting", radish:"brassica", spinach:"brassica",
  squash:"fruiting", sweet_corn:"cereal", swiss_chard:"brassica",
  tomato_fresh:"fruiting", tomato_proc:"fruiting", turnip:"brassica", watermelon:"fruiting",
  // Root / Tubers
  cassava:"root", potato:"root", sweet_potato:"root", yam:"root",
  // Legumes
  bean_dry:"legume", bean_green:"legume", chickpea:"legume", cowpea:"legume",
  fava_bean:"legume", groundnut:"legume", lentil:"legume", soybean:"legume",
  // Cereals
  barley:"cereal", corn_maize:"cereal", millet:"cereal", oat:"cereal",
  quinoa:"cereal", rice:"cereal", rye:"cereal", sorghum:"cereal",
  wheat_winter:"cereal", wheat_spring:"cereal",
  // Oil crops
  canola:"oilseed", cotton:"oilseed", flax:"oilseed", safflower:"oilseed",
  sesame:"oilseed", sunflower:"oilseed",
  // Sugar
  sugarcane:"perennial", sugar_beet:"root",
  // Tropical fruits & perennials
  avocado:"perennial", banana:"perennial", citrus_orange:"perennial",
  citrus_lemon:"perennial", coconut:"perennial", coffee:"perennial",
  mango:"perennial", papaya:"perennial", pineapple:"perennial", tea:"perennial",
  // Deciduous fruits / nuts
  almond:"perennial", apple:"perennial", apricot:"perennial", cherry:"perennial",
  olive:"perennial", peach:"perennial", pear:"perennial", pistachio:"perennial",
  plum:"perennial", walnut:"perennial",
  // Berries / vines
  blueberry:"perennial", grape_table:"perennial", grape_wine:"perennial",
  raspberry:"perennial", strawberry:"fruiting",
  // Forages
  alfalfa:"legume", bermuda_grass:"forage", clover:"legume", ryegrass:"forage",
  // Herbs / specialty
  basil:"herb", cilantro:"herb", hemp:"oilseed", hops:"herb", mint:"herb", tobacco:"oilseed",
};

const ROTATION_AFTER = {
  cereal:   [
    { family:"legume",   reason:"Legumes fix nitrogen depleted by cereals and break cereal root disease cycles." },
    { family:"root",     reason:"Root crops occupy different soil layers and break cereal-specific pathogens." },
    { family:"oilseed",  reason:"Oilseeds have different nutrient uptake and improve soil structure." },
  ],
  legume:   [
    { family:"cereal",   reason:"Cereals use the nitrogen fixed by legumes — highest yield benefit." },
    { family:"root",     reason:"Root crops benefit from improved soil organic matter left by legumes." },
    { family:"brassica", reason:"Brassicas help suppress legume-specific soil pathogens." },
  ],
  root:     [
    { family:"cereal",   reason:"Cereals restore soil structure disturbed by root crop harvest." },
    { family:"legume",   reason:"Legumes replenish nutrients heavily extracted by root crops." },
    { family:"fruiting", reason:"Fruiting crops break root disease cycles and use different soil depths." },
  ],
  brassica: [
    { family:"legume",   reason:"Legumes restore nitrogen depleted by brassicas." },
    { family:"cereal",   reason:"Cereals break brassica clubroot and other soilborne diseases." },
    { family:"root",     reason:"Root crops have minimal disease overlap with brassicas." },
  ],
  fruiting: [
    { family:"cereal",   reason:"Cereals break soilborne diseases common in fruiting crops (Fusarium, Verticillium)." },
    { family:"legume",   reason:"Legumes replenish nitrogen and improve soil biology after heavy fruiting crops." },
  ],
  oilseed:  [
    { family:"cereal",   reason:"Cereals benefit from the deep root channels left by oilseeds." },
    { family:"legume",   reason:"Legumes replenish nitrogen and break oilseed sclerotinia risk." },
  ],
  perennial:[ { family:"cereal", reason:"Perennial crops are best managed in place; rotate understorey if possible." } ],
  forage:   [
    { family:"cereal",  reason:"Cereals follow forages well — residual nitrogen and improved soil structure boost yields." },
    { family:"legume",  reason:"Legumes complement forages by adding nitrogen after the sod is broken." },
  ],
  herb:     [
    { family:"cereal",  reason:"Cereals break herb-specific soil pathogens and utilise residual nutrients." },
    { family:"legume",  reason:"Legumes restore soil fertility depleted by intensive herb production." },
  ],
};

const FAMILY_EXAMPLES = {
  cereal:"(Wheat, Maize, Barley, Sorghum)",
  legume:"(Soybean, Beans, Groundnut)",
  root:"(Potato, Carrot, Onion, Cassava)",
  brassica:"(Cabbage, Kale)",
  fruiting:"(Tomato)",
  oilseed:"(Sunflower)",
  perennial:"(Coffee, Banana, Mango, Grape)",
};

function RecommendationsTab({ org, lang = "en" }) {
  const [analyses, setAnalyses]   = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const headers = { Authorization: `Bearer ${getToken()}` };

  useEffect(() => {
    if (!org) return;
    axios.get(`${API}/analyses/org/${org.id}`, { headers })
      .then(r => {
        const list = Array.isArray(r.data) ? r.data : (r.data?.analyses || []);
        setAnalyses(list);
        if (list.length) setSelectedId(String(list[0].id));
      }).catch(() => {});
  }, [org?.id]);

  const selected  = analyses.find(a => String(a.id) === selectedId);
  const crop      = normalizeCropKey(rowCrop(selected)) || "";
  const cropName  = (crop && CROP_REQ_FE[crop]?.name) || rowCrop(selected) || "";
  const family    = ROTATION_FAMILY[crop] || null;
  const rotations = family ? (ROTATION_AFTER[family] || []) : [];
  const result    = parseResult(selected?.result);
  const recs      = result?.recommendations || result?.management_recommendations || [];
  const ndvi      = rowNdvi(selected);
  const area      = rowArea(selected);

  if (!analyses.length) return <div style={{ color:"#999", fontSize:13 }}>No saved analyses found.</div>;

  return (
    <div>
      <h2 style={{ fontSize:18, fontWeight:700, color:"#1a2e1a", marginBottom:16 }}>Recommendations</h2>

      {/* Field selector */}
      <div style={{ marginBottom:20, display:"flex", alignItems:"center", gap:10 }}>
        <label style={{ fontSize:12, color:"#7a9a6a", fontWeight:600 }}>Field</label>
        <select value={selectedId} onChange={e => setSelectedId(e.target.value)}
          style={{ padding:"6px 10px", fontSize:13, borderRadius:7, border:"1px solid #dde8d0", background:"#fff" }}>
          {analyses.map(a => {
            const c = rowCrop(a);
            return (
              <option key={a.id} value={a.id}>
                {a.name || `#${a.id}`}{c ? ` — ${c}` : ""}
              </option>
            );
          })}
        </select>
      </div>

      {/* Analysis summary */}
      {selected && (
        <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e2ead8", padding:"18px 20px", marginBottom:20 }}>
          <div style={{ fontSize:14, fontWeight:700, color:"#1a2e1a", marginBottom:12 }}>Field Summary</div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))", gap:12 }}>
            {[
              ["Crop",     tCrop(cropName, lang) || "—"],
              ["Area",     area != null ? `${Number(area).toFixed(2)} ha` : "—"],
              ["Health",   result?.health_status || "—"],
              ["NDVI",     ndvi != null ? Number(ndvi).toFixed(3) : "—"],
              ["Analysed", selected.analysis_date || selected.last_analyzed_at?.slice(0,10) || selected.created_at?.slice(0,10) || "—"],
            ].map(([k,v]) => (
              <div key={k} style={{ background:"#f5f9f3", borderRadius:8, padding:"10px 12px" }}>
                <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.07em", color:"#7a9a6a", marginBottom:4 }}>{k}</div>
                <div style={{ fontSize:15, fontWeight:700, color:"#1a2e1a" }}>{v}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Analysis recommendations from result */}
      {recs.length > 0 && (
        <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e2ead8", padding:"18px 20px", marginBottom:20 }}>
          <div style={{ fontSize:14, fontWeight:700, color:"#1a2e1a", marginBottom:12 }}>Field Analysis Recommendations</div>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {(Array.isArray(recs) ? recs : Object.values(recs)).map((r, i) => (
              <div key={i} style={{ display:"flex", gap:10, fontSize:12, color:"#374151", lineHeight:1.6 }}>
                <span style={{ color:"#2d5a1b", fontWeight:700, flexShrink:0 }}>→</span>
                <span>{typeof r === "string" ? r : r.recommendation || r.text || JSON.stringify(r)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Crop rotation planner */}
      <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e2ead8", padding:"18px 20px", marginBottom:20 }}>
        <div style={{ fontSize:14, fontWeight:700, color:"#1a2e1a", marginBottom:4 }}>Crop Rotation Planner</div>
        {!crop ? (
          <div style={{ color:"#999", fontSize:12 }}>Select a field with a saved crop to see rotation recommendations.</div>
        ) : !family ? (
          <div style={{ color:"#999", fontSize:12 }}>No rotation data for {cropName}.</div>
        ) : (
          <>
            <div style={{ fontSize:12, color:"#7a9a6a", marginBottom:14 }}>
              <strong>{cropName}</strong> is a <strong>{family}</strong>. Recommended next crops are shown below ranked by agronomic benefit.
            </div>
            {/* Year timeline */}
            <div style={{ display:"flex", gap:0, marginBottom:20, overflowX:"auto" }}>
              {[`Year 1\n${cropName}`, ...rotations.slice(0,2).map((r,i) => `Year ${i+2}\n${r.family.charAt(0).toUpperCase()+r.family.slice(1)} ${FAMILY_EXAMPLES[r.family]||""}`)].map((yr, i) => (
                <div key={i} style={{ flex:"1 1 120px", minWidth:100, position:"relative" }}>
                  <div style={{
                    background: i===0 ? "#2d5a1b" : i===1 ? "#3b82f6" : "#7c3aed",
                    color:"#fff", borderRadius: i===0?"10px 0 0 10px": i===2?"0 10px 10px 0":"0",
                    padding:"10px 14px", fontSize:11, fontWeight:600, lineHeight:1.4,
                    whiteSpace:"pre-line", textAlign:"center",
                  }}>
                    {yr}
                  </div>
                  {i < 2 && <div style={{ position:"absolute", right:-8, top:"50%", transform:"translateY(-50%)",
                    width:16, height:16, background:"#fff", borderRadius:"50%", border:"2px solid #dde8d0",
                    display:"flex", alignItems:"center", justifyContent:"center", zIndex:1, fontSize:10, color:"#aaa" }}>›</div>}
                </div>
              ))}
            </div>
            {/* Rotation cards */}
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {rotations.map((r, i) => (
                <div key={i} style={{ background:"#f5f9f3", borderRadius:10, padding:"12px 16px", border:"1px solid #e2ead8" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
                    <span style={{ fontSize:13, fontWeight:700, color:"#1a2e1a", textTransform:"capitalize" }}>
                      {r.family}s {FAMILY_EXAMPLES[r.family] || ""}
                    </span>
                    <span style={{ fontSize:11, background:"#2d5a1b22", color:"#2d5a1b",
                      padding:"2px 8px", borderRadius:10, fontWeight:600 }}>
                      Option {i+1}
                    </span>
                  </div>
                  <div style={{ fontSize:12, color:"#374151", lineHeight:1.6 }}>{r.reason}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize:11, color:"#aaa", marginTop:12, lineHeight:1.5 }}>
              General rule: avoid planting the same crop family more than once every 3–4 years in the same field.
              Legumes in the rotation reduce fertilizer needs by fixing atmospheric nitrogen.
            </div>
          </>
        )}
      </div>

      {/* General agronomic tips */}
      <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e2ead8", padding:"18px 20px" }}>
        <div style={{ fontSize:14, fontWeight:700, color:"#1a2e1a", marginBottom:12 }}>General Agronomic Principles</div>
        {[
          ["Soil Health", "Test soil pH and organic matter every 2–3 seasons. Most crops thrive between pH 6.0–7.0. Lime acidic soils; add organic matter (compost) to improve structure and water retention."],
          ["Integrated Pest Management", "Scout fields weekly during the growing season. Identify pests and diseases early. Prefer biological controls and targeted spot-treatments over blanket spraying to preserve beneficial insects."],
          ["Water Management", "Irrigate in early morning (5–8 AM) to reduce evaporation losses by up to 30%. Drip irrigation uses 40–60% less water than flood irrigation. Monitor soil moisture rather than scheduling by calendar."],
          ["Nutrient Timing", "Apply nitrogen in split doses — at planting and at key growth stages — rather than all at once. This reduces leaching losses and improves uptake efficiency."],
          ["Record Keeping", "Track planting dates, inputs, yield, and weather events for each field season. Multi-year records reveal patterns that single-season data cannot."],
        ].map(([title, text]) => (
          <div key={title} style={{ marginBottom:14, paddingBottom:14, borderBottom:"1px solid #f0f0e8" }}>
            <div style={{ fontSize:13, fontWeight:700, color:"#1a2e1a", marginBottom:4 }}>{title}</div>
            <div style={{ fontSize:12, color:"#374151", lineHeight:1.7 }}>{text}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Advanced Analysis Tab ─────────────────────────────────────────────────────
function AdvancedAnalysisTab() {
  const { i18n: i18nHook } = useTranslation();
  const [lang, setLang] = useState(i18nHook.language || "en");
  useEffect(() => {
    const onChange = (lng) => setLang(lng);
    i18nHook.on("languageChanged", onChange);
    return () => i18nHook.off("languageChanged", onChange);
  }, [i18nHook]);
  const embedSrc = `${window.location.origin}/?embed=1&lang=${lang}`;
  return (
    <iframe
      key={lang}
      src={embedSrc}
      title="Field Evaluator"
      style={{ display: "block", width: "100%", height: "calc(100vh - 101px)", border: "none" }}
      allow="geolocation"
    />
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ROOT EXPORT — handles routing between sign-in / magic-link / dashboard
// ══════════════════════════════════════════════════════════════════════════════
export default function Dashboard({ onBack, onDisease }) {
  const [view, setView]   = useState("loading"); // loading | signin | dashboard
  const [user, setUser]   = useState(null);

  // Check for ?token= in URL (magic link click)
  const urlParams = new URLSearchParams(window.location.search);
  const magicToken = urlParams.get("token");

  useEffect(() => {
    const jwt = getToken();
    if (!jwt) { setView(magicToken ? "magic" : "signin"); return; }
    // Validate existing JWT
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${jwt}` } })
      .then(r => { setUser(r.data); setView("dashboard"); })
      .catch(() => { clearToken(); setView(magicToken ? "magic" : "signin"); });
  }, [magicToken]);

  function handleAuth(userData) {
    setUser(userData);
    setView("dashboard");
    // Clean token from URL
    window.history.replaceState({}, "", window.location.pathname);
  }

  if (view === "loading")   return (
    <div style={{ ...S.page }}>
      <div style={{ width: 320 }}>
        <Sk w="45%" h={22} r={8} mb={20} />
        <SkCard lines={4} mb={12} />
        <SkCard lines={3} mb={12} />
        <SkCard lines={2} mb={0} />
      </div>
    </div>
  );
  if (view === "magic")     return <MagicLinkHandler token={magicToken} onAuth={handleAuth} onBack={onBack} />;
  if (view === "signin")    return <SignInPage onAuth={handleAuth} onBack={onBack} />;
  if (view === "dashboard") return <DashboardShell user={user} onSignOut={() => { setUser(null); setView("signin"); }} onDisease={onDisease} />;
  return null;
}
